import { storage } from './lib/storage';
import { Orcamento, ItemOrcamento, PerfilMei, TipoChavePix } from './contracts/schemas';
import { calculateOrcamentoTotals, calculateItemSubtotal, formatCurrency, formatDateBR, getStatusBadgeClass, getStatusLabel } from './lib/orcamento-calc';
import { generateWhatsAppShareUrl, generateQrCodeSvg, validatePixKey, validatePerfilEmpresa } from './lib/pix';
import { buildWhatsAppQuoteMessage, buildCleanWhatsAppQuoteMessage } from './lib/whatsapp-message';
import { printProposal, downloadProposalHTML } from './lib/pdf';
import { getCurrentUser, registerAccount, loginAccount, logout, onAuthChange, initAuthSession, deleteAccountAndUserData } from './lib/auth';
import { syncEmpresaData } from './lib/sync';
import { getLanguage, setLanguage, t } from './lib/i18n';
import { PRESET_LOGOS } from './lib/logo-library';
import { isZapOrcaProActive, onProStatusChange } from './lib/licenca/pro';
import { openZapOrcaProModal } from './lib/licenca/modal-pro';
import { exportProposalsToCSV } from './lib/export-csv';
import { CLAUSULAS_CONTRATUAIS_MEI, temClausula, alternarClausula } from './lib/clausulas-contratuais';
import { CATEGORIAS_SERVICOS_MEI, getCategoriaServicoById } from './lib/catalogo-servicos-mei';

// Ícones SVG inline para interface limpa e moderna
const ICONS = {
  zap: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>`,
  plus: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/></svg>`,
  whatsapp: `<svg class="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.299.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86.173.086.275.072.376-.044.101-.116.433-.506.549-.68.116-.173.231-.144.39-.086s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.099.824z"/></svg>`,
  pix: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4M6 6l12 12M6 18L18 6"/></svg>`,
  check: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>`,
  eye: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>`,
  copy: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>`,
  trash: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>`,
  arrowLeft: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>`,
  arrowRight: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>`,
  print: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/></svg>`,
  shield: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>`,
  sparkles: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>`,
  trend: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>`
};


export class ZapOrcaApp {
  private container: HTMLElement;
  private currentRoute: string = '';
  private activeTab: string = 'todos';
  private wizardStep: number = 1;
  private selectedNichoId: string = 'eletrica';
  private newOrcamentoForm: {
    clienteNome: string;
    clienteTelefone: string;
    clienteEmail: string;
    clienteEndereco: string;
    titulo: string;
    prazoEstimado: string;
    itens: ItemOrcamento[];
    tipoDesconto: 'fixo' | 'percentual';
    desconto: number;
    percentualSinal: number;
    observacoes: string;
    condicoesPagamento: string;
  } = this.getInitialForm();

  constructor(container: HTMLElement) {
    this.container = container;
    this.applyTheme(storage.getTheme());
    this.initRouter();
    initAuthSession().then(() => this.render());
    storage.subscribe(() => {
      this.applyTheme(storage.getTheme());
      this.render();
    });
    onAuthChange(() => {
      this.render();
    });
    onProStatusChange(() => {
      this.render();
    });
  }

  public applyTheme(theme: 'light' | 'dark'): void {
    document.documentElement.setAttribute('data-theme', theme);
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme === 'dark' ? '#10171a' : '#fbf7f0');
    }
    document.documentElement.lang = getLanguage() === 'pt' ? 'pt-BR' : getLanguage();
  }

  public toggleTheme(): void {
    const next = storage.getTheme() === 'light' ? 'dark' : 'light';
    storage.setTheme(next);
    this.applyTheme(next);
    this.showToast(next === 'dark' ? t('themeDarkOn') : t('themeLightOn'), '◐');
  }

  private portalHeaderHTML(): string {
    const lang = getLanguage();
    const theme = storage.getTheme();
    const themeLabel = theme === 'dark' ? t('themeToLight') : t('themeToDark');
    return `
      <!-- Barra Superior Unificada do Portal BRUP -->
      <a class="skip-link" href="#conteudo">Ir para o conteúdo</a>
      <header class="site-header">
        <div class="site-header__inner">
          <a class="brand" href="/" aria-label="Brup Ferramentas — início">
            <span class="brand__mark" aria-hidden="true">BF</span>
            <span>Brup Ferramentas</span>
          </a>
          <div class="site-header__acoes">
            <nav aria-label="Principal">
              <ul class="nav-links">
                <li><a href="/">Início</a></li>
                <li><a href="/ferramentas/">Ferramentas</a></li>
                <li><a href="/quadro/">Quadro</a></li>
                <li><a href="/zaporca/" class="active" aria-current="page">ZapOrca</a></li>
                <li><a href="/vibe-code/">Vibe Code</a></li>
                <li><a href="/sobre.html">Sobre</a></li>
                <li><a href="/privacidade.html">Privacidade</a></li>
              </ul>
            </nav>
            <div class="lang-selector js-only" id="lang-selector">
              <div class="lang-switcher" role="group" aria-label="${t('langLabel')}">
                <button type="button" class="lang-btn${lang === 'pt' ? ' is-active' : ''}" data-lang="pt" aria-label="Português" aria-pressed="${lang === 'pt' ? 'true' : 'false'}">PT</button>
                <button type="button" class="lang-btn${lang === 'en' ? ' is-active' : ''}" data-lang="en" aria-label="English" aria-pressed="${lang === 'en' ? 'true' : 'false'}">EN</button>
                <button type="button" class="lang-btn${lang === 'es' ? ' is-active' : ''}" data-lang="es" aria-label="Español" aria-pressed="${lang === 'es' ? 'true' : 'false'}">ES</button>
              </div>
            </div>
            <button type="button" class="btn-tema js-only" data-tema-toggle aria-pressed="${theme === 'dark' ? 'true' : 'false'}" aria-label="${themeLabel}">
              <span aria-hidden="true">◐</span>
            </button>
          </div>
        </div>
      </header>
    `;
  }

  private portalFooterHTML(): string {
    return `
      <!-- Rodapé Unificado do Portal BRUP -->
      <footer class="site-footer">
        <div class="site-footer__inner">
          <p class="site-footer__copy">© 2026 BRUP · Brup Ferramentas Úteis. 100% no seu navegador.</p>
          <p class="footer-crosslinks">
            Módulos integrados ao ecossistema BRUP: <strong>PDF BRUP</strong> · <strong>ImgFácil</strong> (Edição de Imagens) · <strong>Ferramenta BR</strong> (Cálculos & Texto) · <strong>ZapOrca</strong> · <strong>Guia Vibe Code</strong>
          </p>
          <div class="site-footer__counter" aria-label="Contador de acessos">
            <img id="brup-visits-badge" src="https://hits.sh/brup-ferramentas-uteis.com.br.svg?style=flat-square&amp;label=visitas&amp;color=0d9488&amp;labelColor=1e293b" alt="Contador de visitas do portal BRUP" width="80" height="20" loading="lazy" />
          </div>
          <nav class="site-footer__nav" aria-label="Rodapé">
            <a href="/">Início</a>
            <a href="/ferramentas/">Ferramentas</a>
            <a href="/quadro/">Quadro</a>
            <a href="/zaporca/" aria-current="page">ZapOrca</a>
            <a href="/vibe-code/">Vibe Code</a>
            <a href="/sobre.html">Sobre</a>
            <a href="/privacidade.html">Privacidade</a>
            <a href="/termos.html">Termos de Uso</a>
            <a href="/contato.html">Contato</a>
          </nav>
        </div>
      </footer>
    `;
  }

  private homeLink(): string {
    return `<a class="z-home" href="/" aria-label="${t('home')}">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1z"/></svg>
    </a>`;
  }

  private chromeControls(): string {
    return '';
  }

  private bindChrome(): void {
    this.container.querySelectorAll<HTMLButtonElement>('[data-lang]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const next = btn.getAttribute('data-lang');
        if (next === 'pt' || next === 'en' || next === 'es') {
          setLanguage(next);
          this.render();
        }
      });
    });
    this.container.querySelectorAll<HTMLButtonElement>('[data-theme-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => this.toggleTheme());
    });
  }

  private getInitialForm() {
    return {
      clienteNome: '',
      clienteTelefone: '',
      clienteEmail: '',
      clienteEndereco: '',
      titulo: 'Serviços Especializados de Reforma e Manutenção',
      prazoEstimado: '2 a 3 dias úteis',
      itens: [
        {
          id: `item-${Date.now()}`,
          descricao: 'Mão de obra especializada com garantia',
          quantidade: 1,
          unidade: 'servico',
          valorUnitario: 350,
          tipo: 'servico' as const,
          subtotal: 350
        }
      ],
      tipoDesconto: 'fixo' as const,
      desconto: 0,
      percentualSinal: 30,
      observacoes: CLAUSULAS_CONTRATUAIS_MEI[0].texto,
      condicoesPagamento: '30% de sinal Pix para início e compra de insumos. Saldo na conclusão dos serviços.'
    };
  }

  private initRouter(): void {
    const handleHashChange = () => {
      this.currentRoute = window.location.hash || '#/';
      this.render();
    };

    window.addEventListener('hashchange', handleHashChange);
    this.currentRoute = window.location.hash || '#/';
  }

  public navigateTo(route: string): void {
    window.location.hash = route;
  }

  public showToast(message: string, icon: string = '✓'): void {
    const root = document.getElementById('toast-root');
    if (!root) return;

    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerHTML = `<span class="text-emerald-400 font-bold">${icon}</span> <span>${message}</span>`;
    root.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(-10px)';
      toast.style.transition = 'all 0.25s ease';
      setTimeout(() => toast.remove(), 250);
    }, 3200);
  }

  public render(): void {
    const route = this.currentRoute;

    if (route.startsWith('#/p/')) {
      const uuid = route.replace('#/p/', '');
      this.renderClientView(uuid);
    } else if (route.startsWith('#/proposta/')) {
      const id = route.replace('#/proposta/', '');
      this.renderProposalDetailView(id);
    } else if (route === '#/novo') {
      this.renderWizardView();
    } else if (route === '#/painel') {
      this.renderDashboardView();
    } else {
      this.renderLandingView();
    }
  }

  private renderLandingView(): void {
    this.container.innerHTML = `
      ${this.portalHeaderHTML()}
      <header class="glass-header px-4 py-3.5 sticky top-0 z-20 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          ${this.homeLink()}
          <span class="w-9 h-9 rounded-xl bg-emerald-gradient flex items-center justify-center font-black">Z</span>
          <div>
            <strong class="block text-sm leading-tight">ZapOrca</strong>
            <span class="text-[11px] text-neutral-500">${t('brandSub')}</span>
          </div>
        </div>
        ${this.chromeControls()}
      </header>
      <main class="p-4 pb-28 space-y-4">
        <section class="card p-5 space-y-3">
          <p class="text-[11px] font-bold uppercase tracking-wider text-emerald-700">${t('forProviders')}</p>
          <h1 class="text-2xl font-black leading-tight text-neutral-900">${t('heroTitle')}</h1>
          <p class="text-sm text-neutral-600 leading-relaxed">${t('heroText')}</p>
          <div class="flex flex-col sm:flex-row gap-2 pt-1">
            <button id="btn-landing-novo" class="btn-touch btn-primary font-bold">${t('ctaNew')}</button>
            <button id="btn-landing-painel" class="btn-touch btn-secondary font-bold">${t('ctaPanel')}</button>
          </div>
        </section>
        <section class="grid gap-3 sm:grid-cols-3">
          <article class="card p-4 space-y-1">
            <h2 class="text-sm font-black">${t('cardPhoneTitle')}</h2>
            <p class="text-xs text-neutral-600">${t('cardPhoneText')}</p>
          </article>
          <article class="card p-4 space-y-1">
            <h2 class="text-sm font-black">${t('cardWhatsTitle')}</h2>
            <p class="text-xs text-neutral-600">${t('cardWhatsText')}</p>
          </article>
          <article class="card p-4 space-y-1">
            <h2 class="text-sm font-black">${t('cardPixTitle')}</h2>
            <p class="text-xs text-neutral-600">${t('cardPixText')}</p>
          </article>
        </section>
        <p class="text-xs text-neutral-500 px-1">
          ${t('portalBefore')}
          <a class="font-bold underline" href="https://brup-ferramentas-uteis.com.br/">BRUP Ferramentas Úteis</a>.
          ${t('portalAfter')}
        </p>
      </main>
      ${this.portalFooterHTML()}
    `;
    this.bindChrome();
    this.container.querySelector('#btn-landing-novo')?.addEventListener('click', () => {
      const orcs = storage.getOrcamentos();
      if (!isZapOrcaProActive() && orcs.length >= 5) {
        this.showToast('Você atingiu o teto de 5 propostas do plano Grátis.', '✨');
        openZapOrcaProModal({
          abaInicial: 'pix',
          onAtivado: () => {
            this.showToast('Passaporte ZapOrca Pro ativado com sucesso!', '✨');
            this.render();
          },
        });
        return;
      }
      this.navigateTo('#/novo');
    });
    this.container.querySelector('#btn-landing-painel')?.addEventListener('click', () => this.navigateTo('#/painel'));
  }

  // ==========================================
  // VIEW 1: DASHBOARD MEI MODERNO
  // ==========================================
  private renderDashboardView(): void {
    const perfil = storage.getPerfilMei();
    const orcamentos = storage.getOrcamentos();
    const user = getCurrentUser();

    // Métricas
    const faturamentoTotal = orcamentos.reduce((acc, o) => acc + o.valorTotal, 0);
    const sinalRecebido = orcamentos
      .filter(o => o.status === 'sinal_pago' || o.status === 'concluido')
      .reduce((acc, o) => acc + (o.valorSinal || o.valorTotal), 0);
    const propostasEmAberto = orcamentos.filter(o => o.status === 'enviado' || o.status === 'visualizado').length;
    const taxaConversao = orcamentos.length > 0 
      ? Math.round((orcamentos.filter(o => o.status === 'sinal_pago' || o.status === 'aprovado' || o.status === 'concluido').length / orcamentos.length) * 100) 
      : 0;

    // Filtro
    const filteredOrcamentos = orcamentos.filter(o => {
      if (this.activeTab === 'todos') return true;
      return o.status === this.activeTab;
    });

    const initials = (perfil.nomeFantasia || perfil.nome)
      .split(' ')
      .slice(0, 2)
      .map(n => n[0])
      .join('')
      .toUpperCase();

    this.container.innerHTML = `
      ${this.portalHeaderHTML()}
      <!-- Header MEI Glassmorphic -->
      <header class="glass-header px-4 py-3.5 sticky top-0 z-20 transition-all">
        <div class="flex items-center justify-between">
          ${this.homeLink()}
          <div class="flex items-center gap-3 cursor-pointer group" id="btn-perfil-empresa" title="Clique para editar dados da empresa, logotipo e chave Pix">
            <div class="relative">
              <div class="w-11 h-11 rounded-2xl bg-emerald-gradient flex items-center justify-center text-white font-extrabold text-base shadow-md group-hover:scale-105 transition-transform overflow-hidden">
                ${perfil.logoUrl ? `<img src="${perfil.logoUrl}" alt="Logo" class="w-full h-full object-contain bg-white p-0.5 rounded-xl" />` : (initials || '⚡')}
              </div>
              <span class="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full flex items-center justify-center text-[9px] text-white">✓</span>
            </div>
            <div>
              <div class="flex items-center gap-1.5">
                <h1 class="text-base font-bold text-neutral-900 leading-tight group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors flex items-center gap-1">
                  ${perfil.nomeFantasia || perfil.nome}
                  <span class="text-[10px] text-neutral-400 font-normal">✏️</span>
                </h1>
              </div>
              <p class="text-xs text-neutral-500 font-medium flex items-center gap-1.5">
                <span class="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                MEI • ${perfil.ramoAtividade}
              </p>
            </div>
          </div>
          <div class="z-header-actions">
            ${this.chromeControls()}
            <button id="btn-header-pro" class="btn-touch text-xs font-bold px-3 py-2 rounded-xl transition-all flex items-center gap-1.5 shadow-sm ${
              isZapOrcaProActive()
                ? 'bg-amber-500/20 text-amber-300 border border-amber-400/40 hover:bg-amber-500/30'
                : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white'
            }" title="Passaporte ZapOrca Pro">
              <span>${isZapOrcaProActive() ? '✨ Pro Ativo' : '✨ Obter Pro'}</span>
            </button>
            <button id="btn-auth-user" class="btn-touch text-xs font-bold px-3.5 py-2 rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 text-white transition-all flex items-center gap-1.5 shadow-sm" title="Perfil opcional neste aparelho">
              <span class="text-emerald-400">👤</span>
              <span class="text-white font-bold">${user ? `✓ ${user.nome || user.email.split('@')[0]}` : 'Perfil neste aparelho'}</span>
            </button>
            <button id="btn-novo-orcamento" class="btn-touch btn-primary shadow-sm text-sm">
              ${ICONS.plus}
              <span>${t('newQuote')}</span>
            </button>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="p-4 space-y-6 pb-28">
        <!-- 4 KPI Cards Modernos e Executivos -->
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <!-- Card 1: Faturamento -->
          <div class="metric-kpi-card kpi-faturamento">
            <div class="flex items-center justify-between mb-2">
              <span class="text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Faturamento</span>
              <span class="p-1.5 rounded-lg bg-teal-500/10 text-teal-600 dark:text-teal-400">${ICONS.trend}</span>
            </div>
            <span class="text-xl sm:text-2xl font-black text-neutral-900 dark:text-white tracking-tight block">${formatCurrency(faturamentoTotal)}</span>
            <span class="text-[11px] text-neutral-400 font-medium mt-1 block">${orcamentos.length} propostas no total</span>
          </div>

          <!-- Card 2: Sinal Pix -->
          <div class="metric-kpi-card kpi-sinal">
            <div class="flex items-center justify-between mb-2">
              <span class="text-[11px] font-bold text-emerald-800 dark:text-emerald-400 uppercase tracking-wider">Sinal em Caixa</span>
              <span class="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">${ICONS.zap}</span>
            </div>
            <span class="text-xl sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight block">${formatCurrency(sinalRecebido)}</span>
            <span class="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium mt-1 block flex items-center gap-1">
              ${ICONS.shield} 100% no seu Pix
            </span>
          </div>

          <!-- Card 3: Negociação -->
          <div class="metric-kpi-card kpi-negociando">
            <div class="flex items-center justify-between mb-2">
              <span class="text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Negociando</span>
              <span class="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">${ICONS.eye}</span>
            </div>
            <span class="text-xl sm:text-2xl font-black text-amber-600 dark:text-amber-400 tracking-tight block">${propostasEmAberto}</span>
            <span class="text-[11px] text-neutral-400 font-medium mt-1 block">Aguardando cliente</span>
          </div>

          <!-- Card 4: Taxa de Fechamento -->
          <div class="metric-kpi-card kpi-conversao">
            <div class="flex items-center justify-between mb-2">
              <span class="text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Conversão</span>
              <span class="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">${ICONS.check}</span>
            </div>
            <span class="text-xl sm:text-2xl font-black text-neutral-900 dark:text-white tracking-tight block">${taxaConversao}%</span>
            <span class="text-[11px] text-neutral-400 font-medium mt-1 block">Taxa de fechamento</span>
          </div>
        </div>

        <!-- Filter Segmented Control -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-bold text-neutral-900 dark:text-white uppercase tracking-wider">Propostas & Orçamentos</h2>
            <div class="flex items-center gap-2">
              <button id="btn-exportar-csv" class="btn-touch btn-secondary px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm" title="Exportar propostas para Excel / CSV">
                <span>📊 Exportar CSV</span>
              </button>
              <span class="text-xs font-bold text-neutral-500">${filteredOrcamentos.length} exibidos</span>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-2 pb-1 text-sm">
            ${[
              { id: 'todos', label: 'Todos', count: orcamentos.length },
              { id: 'enviado', label: 'Enviados', count: orcamentos.filter(o => o.status === 'enviado').length },
              { id: 'sinal_pago', label: 'Sinal Pago', count: orcamentos.filter(o => o.status === 'sinal_pago').length },
              { id: 'rascunho', label: 'Rascunhos', count: orcamentos.filter(o => o.status === 'rascunho').length },
              { id: 'concluido', label: 'Concluídos', count: orcamentos.filter(o => o.status === 'concluido').length }
            ].map(tab => `
              <button class="filter-tab px-3.5 py-1.5 rounded-full font-bold text-xs whitespace-nowrap transition-all flex items-center gap-1.5 ${
                this.activeTab === tab.id 
                  ? 'bg-neutral-900 text-white shadow-sm' 
                  : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-100'
              }" data-tab="${tab.id}">
                <span>${tab.label}</span>
                <span class="px-1.5 py-0.2 rounded-full text-[10px] ${this.activeTab === tab.id ? 'bg-neutral-800 text-emerald-400' : 'bg-neutral-100 text-neutral-500'} font-black">${tab.count}</span>
              </button>
            `).join('')}
          </div>
        </div>

        <!-- Proposals Modern Cards List -->
        <div class="space-y-3.5">
          ${filteredOrcamentos.length === 0 ? `
            <div class="card text-center py-14 px-6 space-y-4 border-dashed border-2 border-neutral-200">
              <div class="w-14 h-14 rounded-2xl bg-neutral-100 mx-auto flex items-center justify-center text-neutral-400 text-2xl">
                📄
              </div>
              <div>
                <h3 class="text-base font-bold text-neutral-900">Nenhum orçamento nesta categoria</h3>
                <p class="text-xs text-neutral-500 max-w-xs mx-auto mt-1">Crie propostas rápidas em 3 passos e envie pelo WhatsApp para receber o sinal Pix.</p>
              </div>
              <button id="btn-novo-empty" class="btn-touch btn-primary text-sm shadow-md">
                ${ICONS.plus}
                <span>Criar Novo Orçamento</span>
              </button>
            </div>
          ` : filteredOrcamentos.map(orc => {
            const cliInitials = orc.cliente.nome.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
            return `
            <div class="card card-interactive hover:border-neutral-300 transition-all space-y-3.5" data-id="${orc.id}">
              <!-- Top row: Client info & Status -->
              <div class="flex items-start justify-between gap-2">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl bg-neutral-100 text-neutral-700 font-extrabold text-sm flex items-center justify-center border border-neutral-200 shadow-sm">
                    ${cliInitials}
                  </div>
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-bold text-neutral-900 text-base">${orc.cliente.nome}</span>
                      <span class="${getStatusBadgeClass(orc.status)}">
                        <span class="badge-dot"></span>
                        ${getStatusLabel(orc.status)}
                      </span>
                    </div>
                    <p class="text-xs text-neutral-500 font-medium line-clamp-1">${orc.titulo}</p>
                  </div>
                </div>
                <span class="text-[11px] font-mono font-bold text-neutral-400 bg-neutral-100 px-2 py-0.5 rounded-md">${orc.numero}</span>
              </div>

              <!-- Price Box with Gradient Frame -->
              <div class="bg-neutral-50 rounded-2xl p-3.5 flex items-center justify-between border border-neutral-200/80">
                <div>
                  <span class="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block">Valor Total</span>
                  <span class="text-base font-black text-neutral-900 tracking-tight">${formatCurrency(orc.valorTotal)}</span>
                </div>
                <div class="text-right">
                  <span class="text-[10px] font-bold text-emerald-700 uppercase tracking-wider block flex items-center gap-1 justify-end">
                    ${ICONS.zap} Sinal Pix (${orc.percentualSinal}%)
                  </span>
                  <span class="text-base font-black text-emerald-700 tracking-tight">${formatCurrency(orc.valorSinal)}</span>
                </div>
              </div>

              <!-- Action Bar with SVG Icons -->
              <div class="grid grid-cols-3 gap-2 pt-0.5">
                <button class="btn-touch btn-secondary text-xs px-2.5 py-2 btn-ver-proposta" data-id="${orc.id}">
                  ${ICONS.eye}
                  <span>Detalhes</span>
                </button>
                <button class="btn-touch btn-whatsapp text-xs px-2.5 py-2 btn-zap-share" data-id="${orc.id}">
                  ${ICONS.whatsapp}
                  <span>WhatsApp</span>
                </button>
                <button class="btn-touch btn-secondary text-xs px-2.5 py-2 btn-recebi-sinal text-emerald-700 hover:border-emerald-300" data-id="${orc.id}" title="O app não vê o Pix. Marque só depois de conferir o app do banco.">
                  ${ICONS.zap}
                  <span>Recebi o sinal</span>
                </button>
              </div>
            </div>
          `;}).join('')}
        </div>
      </main>
      ${this.portalFooterHTML()}
    `;

    // Event Listeners
    this.bindChrome();
    this.container.querySelector('#btn-auth-user')?.addEventListener('click', () => this.openAuthModal());
    this.container.querySelector('#btn-perfil-empresa')?.addEventListener('click', () => this.openPerfilEmpresaModal());

    this.container.querySelector('#btn-header-pro')?.addEventListener('click', () => {
      openZapOrcaProModal({
        onAtivado: () => {
          this.showToast('Passaporte ZapOrca Pro ativado com sucesso!', '✨');
          this.render();
        },
        onDesativado: () => {
          this.showToast('Licença Pro desativada.', 'ℹ️');
          this.render();
        },
      });
    });

    this.container.querySelector('#btn-exportar-csv')?.addEventListener('click', () => {
      if (isZapOrcaProActive()) {
        try {
          exportProposalsToCSV(orcamentos);
          this.showToast('Planilha Excel/CSV gerada com sucesso!', '📊');
        } catch {
          this.showToast('Nenhum orçamento disponível para exportação.', '⚠️');
        }
      } else {
        this.showToast('A exportação em planilha é exclusiva do ZapOrca Pro.', '✨');
        openZapOrcaProModal({
          abaInicial: 'pix',
          onAtivado: () => {
            this.showToast('Passaporte ZapOrca Pro ativado com sucesso!', '✨');
            this.render();
          },
        });
      }
    });

    const handleNovoOrcamento = () => {
      if (!isZapOrcaProActive() && orcamentos.length >= 5) {
        this.showToast('Você atingiu o teto de 5 propostas do plano Grátis.', '✨');
        openZapOrcaProModal({
          abaInicial: 'pix',
          onAtivado: () => {
            this.showToast('Passaporte ZapOrca Pro ativado com sucesso!', '✨');
            this.render();
          },
        });
        return;
      }
      this.navigateTo('#/novo');
    };

    this.container.querySelector('#btn-novo-orcamento')?.addEventListener('click', handleNovoOrcamento);
    this.container.querySelector('#btn-novo-empty')?.addEventListener('click', handleNovoOrcamento);

    this.container.querySelectorAll('.filter-tab').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const tab = (e.currentTarget as HTMLElement).dataset.tab;
        if (tab) {
          this.activeTab = tab;
          this.render();
        }
      });
    });

    this.container.querySelectorAll('.btn-ver-proposta').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (id) this.navigateTo(`#/proposta/${id}`);
      });
    });

    this.container.querySelectorAll('.btn-zap-share').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (id) this.openWhatsAppModal(id);
      });
    });

    this.container.querySelectorAll('.btn-recebi-sinal').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (id) {
          const res = storage.simulateWebhookPixPayment(id);
          if (res.success) {
            this.showToast('Marquei que recebi o sinal. Confira no app do banco.', '✓');
          }
        }
      });
    });
  }

  // ==========================================
  // VIEW 2: WIZARD 3 PASSOS MODERNO
  // ==========================================
  private renderWizardView(): void {
    const f = this.newOrcamentoForm;
    const totals = calculateOrcamentoTotals(f.itens, f.desconto, f.tipoDesconto, f.percentualSinal);
    const currentNicho = getCategoriaServicoById(this.selectedNichoId);

    this.container.innerHTML = `
      ${this.portalHeaderHTML()}
      <!-- Header do Wizard -->
      <header class="glass-header px-4 py-3.5 sticky top-0 z-20 flex items-center justify-between">
        ${this.homeLink()}
        <button id="btn-cancelar-wizard" class="btn-touch btn-ghost text-xs font-bold text-neutral-600 px-3 py-1.5">
          ${ICONS.arrowLeft}
          <span>${t('cancel')}</span>
        </button>
        <span class="font-extrabold text-sm text-neutral-900">${t('newQuote')}</span>
        ${this.chromeControls()}
      </header>

      <!-- Stepper Visual Moderno -->
      <div class="bg-white border-b border-neutral-200/80 px-6 py-3.5 shadow-sm">
        <div class="stepper-track max-w-sm mx-auto">
          <!-- Step 1 -->
          <div class="stepper-step">
            <div class="stepper-circle ${this.wizardStep === 1 ? 'stepper-circle-active' : this.wizardStep > 1 ? 'stepper-circle-completed' : 'stepper-circle-inactive'}">
              ${this.wizardStep > 1 ? ICONS.check : '1'}
            </div>
            <span class="text-[11px] font-bold ${this.wizardStep === 1 ? 'text-emerald-700' : 'text-neutral-500'}">Cliente</span>
          </div>

          <div class="flex-1 h-0.5 mx-2 ${this.wizardStep > 1 ? 'bg-emerald-500' : 'bg-neutral-200'}"></div>

          <!-- Step 2 -->
          <div class="stepper-step">
            <div class="stepper-circle ${this.wizardStep === 2 ? 'stepper-circle-active' : this.wizardStep > 2 ? 'stepper-circle-completed' : 'stepper-circle-inactive'}">
              ${this.wizardStep > 2 ? ICONS.check : '2'}
            </div>
            <span class="text-[11px] font-bold ${this.wizardStep === 2 ? 'text-emerald-700' : 'text-neutral-500'}">Serviços</span>
          </div>

          <div class="flex-1 h-0.5 mx-2 ${this.wizardStep > 2 ? 'bg-emerald-500' : 'bg-neutral-200'}"></div>

          <!-- Step 3 -->
          <div class="stepper-step">
            <div class="stepper-circle ${this.wizardStep === 3 ? 'stepper-circle-active' : 'stepper-circle-inactive'}">
              3
            </div>
            <span class="text-[11px] font-bold ${this.wizardStep === 3 ? 'text-emerald-700' : 'text-neutral-500'}">Sinal Pix</span>
          </div>
        </div>
      </div>

      <main class="p-4 space-y-5 pb-32 flex-1">
        ${this.wizardStep === 1 ? `
          <!-- PASSO 1: DADOS DO CLIENTE -->
          <div class="card space-y-4">
            <div class="border-b border-neutral-100 pb-3">
              <h2 class="text-base font-extrabold text-neutral-900">Identificação da Proposta</h2>
              <p class="text-xs text-neutral-500">Dados do cliente para envio direto no WhatsApp.</p>
            </div>
            
            <div>
              <label class="block text-xs font-bold text-neutral-700 uppercase tracking-wider mb-1.5">Nome do Cliente *</label>
              <input type="text" id="wiz-nome" class="input-touch font-medium" placeholder="Ex: Mariana Souza" value="${f.clienteNome}" />
            </div>

            <div>
              <label class="block text-xs font-bold text-neutral-700 uppercase tracking-wider mb-1.5">WhatsApp com DDD *</label>
              <input type="tel" id="wiz-tel" class="input-touch font-mono font-medium" placeholder="Ex: 11987654321" value="${f.clienteTelefone}" />
              <span class="text-[11px] text-neutral-400 mt-1 block">Apenas números com DDD para disparo direto via WhatsApp.</span>
            </div>

            <div>
              <label class="block text-xs font-bold text-neutral-700 uppercase tracking-wider mb-1.5">Título / Objeto do Serviço *</label>
              <input type="text" id="wiz-titulo" class="input-touch font-medium" placeholder="Ex: Reforma do Quadro Elétrico e Ponto de Ar" value="${f.titulo}" />
            </div>

            <div>
              <label class="block text-xs font-bold text-neutral-700 uppercase tracking-wider mb-1.5">Endereço da Obra (Opcional)</label>
              <input type="text" id="wiz-endereco" class="input-touch font-medium" placeholder="Ex: Rua das Flores, 120 - Pinheiros" value="${f.clienteEndereco}" />
            </div>

            <div>
              <label class="block text-xs font-bold text-neutral-700 uppercase tracking-wider mb-1.5">Prazo Estimado de Execução</label>
              <input type="text" id="wiz-prazo" class="input-touch font-medium" placeholder="Ex: 2 dias úteis" value="${f.prazoEstimado}" />
            </div>
          </div>
        ` : this.wizardStep === 2 ? `
          <!-- PASSO 2: ITENS E SERVIÇOS -->
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-base font-extrabold text-neutral-900">Mão de Obra e Materiais</h2>
                <p class="text-xs text-neutral-500">Adicione os serviços inclusos na proposta.</p>
              </div>
              <span class="px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-700 font-bold text-xs">${f.itens.length} item(ns)</span>
            </div>

            <!-- Seletor de Nicho MEI e Sugestões Prontas (1-Toque) -->
            <div class="card bg-emerald-50/70 dark:bg-emerald-950/20 border-emerald-200/90 dark:border-emerald-800/40 p-4 space-y-3 shadow-sm">
              <div class="flex items-center justify-between">
                <span class="text-xs font-black text-emerald-950 dark:text-emerald-300 uppercase tracking-wider flex items-center gap-1.5">
                  ${ICONS.sparkles} Serviços Sugeridos por Profissão / Nicho:
                </span>
                <span class="text-[11px] text-emerald-800 dark:text-emerald-400 font-semibold">1-Toque para adicionar</span>
              </div>
              
              <!-- Pílulas de Nichos -->
              <div class="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar" id="nicho-tabs">
                ${CATEGORIAS_SERVICOS_MEI.map(cat => `
                  <button type="button" class="btn-nicho-tab whitespace-nowrap px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                    this.selectedNichoId === cat.id
                      ? 'bg-emerald-700 text-white shadow-sm border border-emerald-700'
                      : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-emerald-100/60 border border-emerald-200 dark:border-neutral-700'
                  }" data-nicho="${cat.id}">
                    <span>${cat.icone}</span>
                    <span>${cat.nome}</span>
                  </button>
                `).join('')}
              </div>

              <!-- Serviços do Nicho Atual -->
              <div class="flex flex-wrap gap-2 pt-1">
                ${currentNicho.servicos.map((p, idx) => `
                  <button type="button" class="btn-preset-nicho bg-white dark:bg-neutral-800 text-emerald-950 dark:text-neutral-100 border border-emerald-300 dark:border-emerald-800 rounded-xl px-2.5 py-1.5 text-xs font-bold hover:bg-emerald-100 dark:hover:bg-neutral-700 hover:border-emerald-500 transition-all shadow-subtle flex items-center gap-1.5 cursor-pointer" data-nicho="${currentNicho.id}" data-idx="${idx}" title="Adicionar ${p.descricao}">
                    ${ICONS.plus}
                    <span>${p.descricao} (${formatCurrency(p.vUnit)} / ${p.unidade})</span>
                  </button>
                `).join('')}
              </div>

              <div class="pt-1 flex justify-between items-center text-xs border-t border-emerald-200/60 dark:border-emerald-800/40">
                <span class="text-[11px] text-neutral-500 dark:text-neutral-400">Valores médios sugeridos (100% editáveis abaixo)</span>
                <button type="button" id="btn-add-todos-nicho" class="text-xs font-black text-emerald-800 dark:text-emerald-300 hover:underline cursor-pointer">
                  + Adicionar Pacote Completo (${currentNicho.servicos.length} itens)
                </button>
              </div>
            </div>

            <!-- Lista de Itens -->
            <div class="space-y-3.5" id="itens-container">
              ${f.itens.map((item, idx) => `
                <div class="card p-4 space-y-3 relative border-neutral-200">
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-black text-emerald-700 uppercase tracking-wider">Item #${idx + 1}</span>
                    ${f.itens.length > 1 ? `
                      <button class="text-red-500 hover:text-red-700 text-xs font-bold btn-remover-item flex items-center gap-1" data-idx="${idx}">
                        ${ICONS.trash}
                        <span>Remover</span>
                      </button>
                    ` : ''}
                  </div>

                  <div>
                    <label class="block text-[11px] font-bold text-neutral-600 uppercase mb-1">Descrição do Serviço / Material</label>
                    <input type="text" class="input-touch item-desc" data-idx="${idx}" value="${item.descricao}" placeholder="Ex: Substituição de disjuntores DIN" />
                  </div>

                  <div class="grid grid-cols-3 gap-2.5">
                    <div>
                      <label class="block text-[11px] font-bold text-neutral-600 uppercase mb-1">Qtd</label>
                      <input type="number" step="0.5" min="0.1" class="input-touch item-qtd text-center font-bold" data-idx="${idx}" value="${item.quantidade}" />
                    </div>
                    <div>
                      <label class="block text-[11px] font-bold text-neutral-600 uppercase mb-1">Unidade</label>
                      <input type="text" class="input-touch item-un text-center font-medium" data-idx="${idx}" value="${item.unidade}" placeholder="un, pt, m²" />
                    </div>
                    <div>
                      <label class="block text-[11px] font-bold text-neutral-600 uppercase mb-1">Valor Unit. (R$)</label>
                      <input type="number" step="10" min="0" class="input-touch item-vunit font-mono font-bold" data-idx="${idx}" value="${item.valorUnitario}" />
                    </div>
                  </div>

                  <div class="flex justify-between items-center pt-2 border-t border-neutral-100 text-xs">
                    <span class="text-neutral-500 font-medium">Subtotal deste item:</span>
                    <strong class="text-neutral-900 font-black text-sm">${formatCurrency(item.subtotal)}</strong>
                  </div>
                </div>
              `).join('')}
            </div>

            <button id="btn-add-item-vazio" class="btn-touch btn-secondary w-full text-sm font-bold border-dashed border-2">
              ${ICONS.plus}
              <span>Adicionar Outro Item Manualmente</span>
            </button>
          </div>
        ` : `
          <!-- PASSO 3: CONDIÇÕES & SINAL PIX -->
          <div class="card space-y-5">
            <div class="border-b border-neutral-100 pb-3">
              <h2 class="text-base font-extrabold text-neutral-900">Condições & Chave Pix de Sinal</h2>
              <p class="text-xs text-neutral-500">Defina o percentual de sinal Pix para agendamento.</p>
            </div>

            <!-- Seletor de Sinal Pix com Pílulas Modernas -->
            <div>
              <label class="block text-xs font-bold text-neutral-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                ${ICONS.zap} Sinal Pix para Reserva de Data:
              </label>
              <div class="grid grid-cols-4 gap-2">
                ${[0, 30, 50, 100].map(p => `
                  <button class="btn-touch py-2.5 text-sm font-extrabold rounded-2xl border transition-all ${
                    f.percentualSinal === p 
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-md transform scale-102' 
                      : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'
                  } btn-set-sinal" data-sinal="${p}">
                    ${p === 0 ? 'Sem Sinal' : `${p}%`}
                  </button>
                `).join('')}
              </div>
            </div>

            <!-- Desconto -->
            <div class="grid grid-cols-2 gap-3 bg-neutral-50 p-3.5 rounded-2xl border border-neutral-200">
              <div>
                <label class="block text-xs font-bold text-neutral-700 uppercase mb-1">Desconto</label>
                <input type="number" id="wiz-desconto" class="input-touch font-bold" min="0" value="${f.desconto}" />
              </div>
              <div>
                <label class="block text-xs font-bold text-neutral-700 uppercase mb-1">Tipo de Desconto</label>
                <select id="wiz-tipo-desconto" class="input-touch font-bold bg-white">
                  <option value="fixo" ${f.tipoDesconto === 'fixo' ? 'selected' : ''}>R$ Valor Fixo</option>
                  <option value="percentual" ${f.tipoDesconto === 'percentual' ? 'selected' : ''}>% Percentual</option>
                </select>
              </div>
            </div>

            <!-- Seletor de Cláusulas Contratuais Prontas (1-Clique MEI) -->
            <div class="bg-emerald-50/70 dark:bg-emerald-950/20 p-4 rounded-2xl border border-emerald-200/80 dark:border-emerald-800/40 space-y-2.5">
              <div class="flex items-center justify-between">
                <label class="text-xs font-black text-emerald-900 dark:text-emerald-300 uppercase tracking-wider flex items-center gap-1.5">
                  ${ICONS.shield} Cláusulas Contratuais Prontas (1-Clique):
                </label>
                <button type="button" id="btn-limpar-clausulas" class="text-[11px] text-neutral-500 hover:text-red-600 dark:text-neutral-400 font-bold transition-colors cursor-pointer" title="Limpar observações e termos">
                  Limpar Termos
                </button>
              </div>
              <p class="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
                Toque nos botões para incluir ou remover proteções jurídicas prontas (CDC e Código Civil):
              </p>
              <div class="flex flex-wrap gap-2 pt-1" id="clausulas-chips-container">
                ${CLAUSULAS_CONTRATUAIS_MEI.map(c => {
                  const ativa = temClausula(f.observacoes, c.id);
                  return `
                    <button type="button" 
                      class="btn-clausula-chip btn-touch py-1.5 px-3 text-xs font-bold rounded-xl border transition-all flex items-center gap-1.5 cursor-pointer ${
                        ativa 
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm' 
                          : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 border-neutral-300 dark:border-neutral-700 hover:border-emerald-400'
                      }" 
                      data-id="${c.id}" 
                      title="${c.descricaoCurta}">
                      <span>${c.icone}</span>
                      <span>${c.titulo}</span>
                      <span class="text-[11px] font-black">${ativa ? '✓' : '+'}</span>
                    </button>
                  `;
                }).join('')}
              </div>
            </div>

            <!-- Observações & Garantia -->
            <div>
              <div class="flex justify-between items-center mb-1">
                <label class="block text-xs font-bold text-neutral-700 dark:text-neutral-300 uppercase">Termos, Prazos e Garantia (Texto Final)</label>
                <span class="text-[11px] text-neutral-400">Editável livremente</span>
              </div>
              <textarea id="wiz-obs" class="input-touch text-xs leading-relaxed" rows="4" placeholder="Ex: Prazos, garantias e condições acordadas com o cliente...">${f.observacoes}</textarea>
            </div>

            <!-- Resumo Financeiro da Proposta -->
            <div class="bg-dark-gradient text-white p-5 rounded-2xl space-y-2.5 shadow-xl">
              <div class="flex justify-between text-xs text-neutral-400 font-medium">
                <span>Subtotal Itens:</span>
                <span class="text-neutral-200 font-bold">${formatCurrency(totals.subtotal)}</span>
              </div>
              ${totals.valorDesconto > 0 ? `
                <div class="flex justify-between text-xs text-red-400 font-bold">
                  <span>Desconto Aplicado:</span>
                  <span>- ${formatCurrency(totals.valorDesconto)}</span>
                </div>
              ` : ''}
              <div class="flex justify-between text-sm font-bold border-t border-neutral-700/80 pt-2.5">
                <span>Valor Total da Proposta:</span>
                <span class="text-white text-lg font-black">${formatCurrency(totals.valorTotal)}</span>
              </div>
              <div class="flex justify-between text-base font-black text-emerald-400 border-t border-neutral-700/80 pt-2.5">
                <span class="flex items-center gap-1.5">
                  ${ICONS.zap} Sinal Pix (${f.percentualSinal}%):
                </span>
                <span class="text-lg">${formatCurrency(totals.valorSinal)}</span>
              </div>
              <div class="flex justify-between text-xs text-neutral-400 pt-1">
                <span>Saldo na conclusão da obra:</span>
                <span class="font-bold text-neutral-300">${formatCurrency(totals.valorRestante)}</span>
              </div>
            </div>
          </div>
        `}
      </main>

      <!-- Sticky Navigation Bar Moderno -->
      <div class="wizard-sticky-bar glass-header p-4 fixed bottom-0 left-0 right-0 max-w-4xl mx-auto flex gap-3 z-30 shadow-lg">
        ${this.wizardStep > 1 ? `
          <button id="btn-voltar-passo" class="btn-touch btn-secondary flex-1 font-bold">
            ${ICONS.arrowLeft}
            <span>Voltar</span>
          </button>
        ` : ''}
        
        ${this.wizardStep < 3 ? `
          <button id="btn-proximo-passo" class="btn-touch btn-primary flex-1 font-bold">
            <span>Avançar Passo</span>
            ${ICONS.arrowRight}
          </button>
        ` : `
          <button id="btn-salvar-orcamento" class="btn-touch btn-whatsapp flex-1 font-black text-base shadow-xl">
            ${ICONS.zap}
            <span>Gerar Proposta & Pix</span>
          </button>
        `}
      </div>
      ${this.portalFooterHTML()}
    `;

    // Event Bindings
    this.bindChrome();
    this.container.querySelector('#btn-cancelar-wizard')?.addEventListener('click', () => {
      this.wizardStep = 1;
      this.navigateTo('#/painel');
    });

    this.container.querySelector('#btn-voltar-passo')?.addEventListener('click', () => {
      this.saveStepInputs();
      this.wizardStep--;
      this.render();
    });

    this.container.querySelector('#btn-proximo-passo')?.addEventListener('click', () => {
      this.saveStepInputs();
      if (this.wizardStep === 1) {
        if (!f.clienteNome || !f.clienteTelefone) {
          alert('Por favor, preencha o Nome e o WhatsApp do cliente.');
          return;
        }
      }
      this.wizardStep++;
      this.render();
    });

    this.container.querySelector('#btn-salvar-orcamento')?.addEventListener('click', () => {
      this.saveStepInputs();
      this.finishAndSaveWizard();
    });

    // Step 2 nicho tabs e presets
    this.container.querySelectorAll('.btn-nicho-tab').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.saveStepInputs();
        const nicho = (e.currentTarget as HTMLElement).dataset.nicho;
        if (nicho) {
          this.selectedNichoId = nicho;
          this.render();
        }
      });
    });

    this.container.querySelectorAll('.btn-preset-nicho').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.saveStepInputs();
        const nichoId = (e.currentTarget as HTMLElement).dataset.nicho;
        const idx = Number((e.currentTarget as HTMLElement).dataset.idx);
        const nicho = getCategoriaServicoById(nichoId || this.selectedNichoId);
        const preset = nicho.servicos[idx];
        if (preset) {
          const novoItem: ItemOrcamento = {
            id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
            descricao: preset.descricao,
            quantidade: 1,
            unidade: preset.unidade,
            valorUnitario: preset.vUnit,
            tipo: preset.tipo,
            subtotal: preset.vUnit
          };
          if (f.itens.length === 1 && (!f.itens[0].descricao || f.itens[0].descricao === 'Mão de obra especializada com garantia')) {
            f.itens = [novoItem];
          } else {
            f.itens.push(novoItem);
          }
          this.showToast(`${preset.descricao} adicionado!`, '✓');
          this.render();
        }
      });
    });

    this.container.querySelector('#btn-add-todos-nicho')?.addEventListener('click', () => {
      this.saveStepInputs();
      const nicho = getCategoriaServicoById(this.selectedNichoId);
      const novosItens: ItemOrcamento[] = nicho.servicos.map((s, i) => ({
        id: `item-${Date.now()}-${i}-${Math.random().toString(36).substring(2, 6)}`,
        descricao: s.descricao,
        quantidade: 1,
        unidade: s.unidade,
        valorUnitario: s.vUnit,
        tipo: s.tipo,
        subtotal: s.vUnit
      }));
      if (f.itens.length === 1 && (!f.itens[0].descricao || f.itens[0].descricao === 'Mão de obra especializada com garantia')) {
        f.itens = novosItens;
      } else {
        f.itens.push(...novosItens);
      }
      this.showToast(`Pacote de ${nicho.nome} adicionado (${novosItens.length} itens)!`, '✓');
      this.render();
    });

    this.container.querySelector('#btn-add-item-vazio')?.addEventListener('click', () => {
      this.saveStepInputs();
      f.itens.push({
        id: `item-${Date.now()}`,
        descricao: '',
        quantidade: 1,
        unidade: 'un',
        valorUnitario: 100,
        tipo: 'servico',
        subtotal: 100
      });
      this.render();
    });

    this.container.querySelectorAll('.btn-remover-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idx = Number((e.currentTarget as HTMLElement).dataset.idx);
        f.itens.splice(idx, 1);
        this.render();
      });
    });

    // Step 3 set sinal
    this.container.querySelectorAll('.btn-set-sinal').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const sinal = Number((e.currentTarget as HTMLElement).dataset.sinal);
        f.percentualSinal = sinal;
        this.render();
      });
    });

    // Step 3 cláusulas contratuais prontas (1-clique)
    this.container.querySelectorAll('.btn-clausula-chip').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.saveStepInputs();
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (id) {
          f.observacoes = alternarClausula(f.observacoes, id);
          this.render();
        }
      });
    });

    this.container.querySelector('#btn-limpar-clausulas')?.addEventListener('click', () => {
      this.saveStepInputs();
      f.observacoes = '';
      this.render();
    });
  }

  private saveStepInputs(): void {
    const f = this.newOrcamentoForm;
    if (this.wizardStep === 1) {
      f.clienteNome = (this.container.querySelector('#wiz-nome') as HTMLInputElement)?.value || f.clienteNome;
      f.clienteTelefone = (this.container.querySelector('#wiz-tel') as HTMLInputElement)?.value || f.clienteTelefone;
      f.titulo = (this.container.querySelector('#wiz-titulo') as HTMLInputElement)?.value || f.titulo;
      f.clienteEndereco = (this.container.querySelector('#wiz-endereco') as HTMLInputElement)?.value || f.clienteEndereco;
      f.prazoEstimado = (this.container.querySelector('#wiz-prazo') as HTMLInputElement)?.value || f.prazoEstimado;
    } else if (this.wizardStep === 2) {
      f.itens.forEach((item, idx) => {
        const descInput = this.container.querySelector(`.item-desc[data-idx="${idx}"]`) as HTMLInputElement;
        const qtdInput = this.container.querySelector(`.item-qtd[data-idx="${idx}"]`) as HTMLInputElement;
        const unInput = this.container.querySelector(`.item-un[data-idx="${idx}"]`) as HTMLInputElement;
        const vunitInput = this.container.querySelector(`.item-vunit[data-idx="${idx}"]`) as HTMLInputElement;

        if (descInput) item.descricao = descInput.value;
        if (qtdInput) item.quantidade = Number(qtdInput.value) || 1;
        if (unInput) item.unidade = unInput.value || 'un';
        if (vunitInput) item.valorUnitario = Number(vunitInput.value) || 0;
        item.subtotal = calculateItemSubtotal(item);
      });
    } else if (this.wizardStep === 3) {
      const descInput = this.container.querySelector('#wiz-desconto') as HTMLInputElement;
      const tipoDescInput = this.container.querySelector('#wiz-tipo-desconto') as HTMLSelectElement;
      const obsInput = this.container.querySelector('#wiz-obs') as HTMLTextAreaElement;

      if (descInput) f.desconto = Number(descInput.value) || 0;
      if (tipoDescInput) f.tipoDesconto = tipoDescInput.value as 'fixo' | 'percentual';
      if (obsInput) f.observacoes = obsInput.value;
    }
  }

  private finishAndSaveWizard(): void {
    const f = this.newOrcamentoForm;
    const perfil = storage.getPerfilMei();
    const totals = calculateOrcamentoTotals(f.itens, f.desconto, f.tipoDesconto, f.percentualSinal);
    const uuid = 'orc-' + Math.random().toString(36).substring(2, 10) + '-' + Date.now();
    const numero = `ORC-2026-${String(storage.getOrcamentos().length + 1).padStart(3, '0')}`;

    const novoOrcamento: Orcamento = {
      id: `orc-${Date.now()}`,
      uuid,
      numero,
      titulo: f.titulo || 'Serviços Especializados',
      dataCriacao: new Date().toISOString(),
      dataValidade: new Date(Date.now() + 10 * 86400000).toISOString(),
      perfilMei: perfil,
      cliente: {
        id: `cli-${Date.now()}`,
        nome: f.clienteNome,
        telefone: f.clienteTelefone,
        email: f.clienteEmail,
        endereco: f.clienteEndereco,
        cidade: perfil.cidade
      },
      itens: f.itens,
      valorSubtotal: totals.subtotal,
      desconto: totals.valorDesconto,
      tipoDesconto: f.tipoDesconto,
      valorTotal: totals.valorTotal,
      percentualSinal: f.percentualSinal,
      valorSinal: totals.valorSinal,
      valorRestante: totals.valorRestante,
      observacoes: f.observacoes,
      condicoesPagamento: f.condicoesPagamento,
      prazoEstimado: f.prazoEstimado,
      status: 'rascunho',
      linkPublico: `#/p/${uuid}`,
      historicoEventos: [
        {
          dataHora: new Date().toISOString(),
          tipo: 'criado',
          descricao: 'Orçamento criado pelo MEI'
        }
      ]
    };

    const saved = storage.saveOrcamento(novoOrcamento);
    this.wizardStep = 1;
    this.newOrcamentoForm = this.getInitialForm();
    this.showToast('Orçamento criado com sucesso!', '✓');
    this.navigateTo(`#/proposta/${saved.id}`);
  }

  // ==========================================
  // VIEW 3: DETALHES DA PROPOSTA MEI MODERNO
  // ==========================================
  private renderProposalDetailView(id: string): void {
    const orc = storage.getOrcamentoById(id);
    if (!orc) {
      this.container.innerHTML = `
        <div class="p-12 text-center space-y-4">
          <p class="text-neutral-500 font-medium">Orçamento não encontrado.</p>
          <button class="btn-touch btn-secondary" onclick="window.location.hash='#/painel'">Voltar ao Painel</button>
        </div>
      `;
      return;
    }

    this.container.innerHTML = `
      ${this.portalHeaderHTML()}
      <!-- Header Detalhes -->
      <header class="glass-header px-4 py-3.5 sticky top-0 z-20 flex items-center justify-between">
        ${this.homeLink()}
        <button id="btn-voltar-painel" class="btn-touch btn-ghost text-xs font-bold text-neutral-600 px-3 py-1.5">
          ${ICONS.arrowLeft}
          <span>${t('panel')}</span>
        </button>
        <span class="font-extrabold text-sm text-neutral-900 dark:text-white font-mono">${orc.numero}</span>
        <div class="flex items-center gap-1.5 flex-wrap justify-end">
          ${this.chromeControls()}
          <button id="btn-duplicar-orcamento" class="btn-touch btn-secondary text-xs px-2.5 py-1.5 font-bold" title="Duplicar proposta para outro cliente">
            <span>📋 Duplicar</span>
          </button>
          <button id="btn-baixar-html" class="btn-touch btn-secondary text-xs px-2.5 py-1.5 font-bold" title="Baixar arquivo HTML">
            ${ICONS.copy}
            <span>HTML</span>
          </button>
          <button id="btn-imprimir-topo" class="btn-touch btn-primary text-xs px-2.5 py-1.5 font-bold" title="Imprimir ou Salvar em PDF">
            ${ICONS.print}
            <span>PDF</span>
          </button>
          <button id="btn-excluir-orcamento" class="btn-touch btn-secondary text-xs px-2 py-1.5 font-bold text-red-600 hover:border-red-300" title="Excluir este orçamento">
            ${ICONS.trash}
          </button>
        </div>
      </header>

      <main class="p-4 space-y-5 pb-32">
        <!-- Status Card com Badges e Logo -->
        <div class="card space-y-3">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-neutral-400 uppercase tracking-wider">Status Atual</span>
            <span class="${getStatusBadgeClass(orc.status)}">
              <span class="badge-dot"></span>
              ${getStatusLabel(orc.status)}
            </span>
          </div>
          <div class="flex items-start gap-3">
            ${orc.perfilMei.logoUrl ? `
              <img src="${orc.perfilMei.logoUrl}" alt="Logo" class="w-12 h-12 rounded-xl object-contain bg-white dark:bg-neutral-800 p-1 border border-neutral-200 dark:border-neutral-700 shrink-0 shadow-sm" />
            ` : ''}
            <div class="flex-1 min-w-0">
              <h2 class="text-lg font-black text-neutral-900 dark:text-white leading-snug truncate">${orc.titulo}</h2>
              <div class="flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-400 pt-1">
                <span class="font-bold text-neutral-900 dark:text-white truncate">Cliente: ${orc.cliente.nome}</span>
                <span>•</span>
                <span class="font-mono">${orc.cliente.telefone}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Totals Card Dark Mesh -->
        <div class="card bg-dark-gradient text-white p-5 space-y-3.5 shadow-xl">
          <div class="flex justify-between items-center">
            <span class="text-xs text-neutral-400 font-medium">Total dos Serviços:</span>
            <span class="text-2xl font-black text-white">${formatCurrency(orc.valorTotal)}</span>
          </div>
          <div class="flex justify-between items-center text-emerald-400 border-t border-neutral-700/80 pt-3 font-extrabold text-base">
            <span class="flex items-center gap-1.5">
              ${ICONS.zap} Sinal Pix (${orc.percentualSinal}%):
            </span>
            <span class="text-xl">${formatCurrency(orc.valorSinal)}</span>
          </div>
          <div class="flex justify-between items-center text-xs text-neutral-400 pt-0.5">
            <span>Saldo Restante na Entrega:</span>
            <span class="text-neutral-200 font-bold">${formatCurrency(orc.valorRestante)}</span>
          </div>
        </div>

        <!-- Botões de Ação Imediata -->
        <div class="space-y-2.5">
          <button id="btn-detalhe-zap" class="btn-touch btn-whatsapp w-full text-base font-extrabold shadow-md">
            ${ICONS.whatsapp}
            <span>Compartilhar Proposta no WhatsApp</span>
          </button>
          <div class="grid grid-cols-2 gap-2.5">
            <button id="btn-detalhe-copiar-link" class="btn-touch btn-secondary text-xs font-bold">
              ${ICONS.copy}
              <span>Copiar Link Cliente</span>
            </button>
            <button id="btn-recebi-sinal-detalhe" class="btn-touch btn-secondary text-xs font-bold text-emerald-700 hover:border-emerald-300" title="O app não vê o Pix. Marque só depois de conferir o app do banco.">
              ${ICONS.zap}
              <span>Recebi o sinal</span>
            </button>
          </div>
        </div>

        <!-- Itens discriminados -->
        <div class="card space-y-3">
          <div class="flex items-center justify-between border-b border-neutral-100 pb-2.5">
            <h3 class="text-sm font-bold text-neutral-900">Itens e Serviços Inclusos</h3>
            <span class="text-xs font-bold text-neutral-400">${orc.itens.length} item(ns)</span>
          </div>
          <div class="space-y-2.5 divide-y divide-neutral-100">
            ${orc.itens.map(item => `
              <div class="pt-2.5 flex justify-between items-start text-xs">
                <div>
                  <span class="font-bold text-neutral-800 block text-sm">${item.descricao}</span>
                  <span class="text-neutral-500 font-medium">${item.quantidade} ${item.unidade} × ${formatCurrency(item.valorUnitario)}</span>
                </div>
                <strong class="text-neutral-900 font-black text-sm">${formatCurrency(item.subtotal)}</strong>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Linha do Tempo de Eventos -->
        <div class="card space-y-3.5">
          <h3 class="text-sm font-bold text-neutral-900 border-b border-neutral-100 pb-2.5">Histórico de Eventos</h3>
          <div class="space-y-0">
            ${orc.historicoEventos.map((ev, idx) => `
              <div class="timeline-item">
                ${idx < orc.historicoEventos.length - 1 ? '<div class="timeline-line"></div>' : ''}
                <div class="timeline-dot"></div>
                <div class="text-xs">
                  <div class="font-bold text-neutral-900">${ev.descricao}</div>
                  <div class="text-neutral-400 font-mono text-[11px] mt-0.5">${formatDateBR(ev.dataHora)}</div>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      </main>
      ${this.portalFooterHTML()}
    `;

    // Event Bindings
    this.bindChrome();
    this.container.querySelector('#btn-voltar-painel')?.addEventListener('click', () => this.navigateTo('#/painel'));
    this.container.querySelector('#btn-imprimir-topo')?.addEventListener('click', () => printProposal(orc));
    this.container.querySelector('#btn-baixar-html')?.addEventListener('click', () => downloadProposalHTML(orc));
    this.container.querySelector('#btn-duplicar-orcamento')?.addEventListener('click', () => {
      const duplicado = storage.duplicateOrcamento(orc.id);
      if (duplicado) {
        this.showToast('Proposta duplicada com sucesso!', '📋');
        this.navigateTo(`#/proposta/${duplicado.id}`);
      }
    });
    this.container.querySelector('#btn-excluir-orcamento')?.addEventListener('click', () => {
      if (confirm(`Deseja realmente excluir a proposta ${orc.numero}?`)) {
        storage.deleteOrcamento(orc.id);
        this.showToast('Proposta excluída.', '🗑️');
        this.navigateTo('#/painel');
      }
    });
    this.container.querySelector('#btn-detalhe-zap')?.addEventListener('click', () => this.openWhatsAppModal(orc.id));
    this.container.querySelector('#btn-detalhe-copiar-link')?.addEventListener('click', () => {
      const fullUrl = `${window.location.origin}${window.location.pathname}#/p/${orc.uuid}`;
      navigator.clipboard.writeText(fullUrl);
      this.showToast('Link do cliente copiado!', '🔗');
    });
    this.container.querySelector('#btn-recebi-sinal-detalhe')?.addEventListener('click', () => {
      storage.simulateWebhookPixPayment(orc.id);
      this.showToast('Marquei que recebi o sinal. Confira no app do banco.', '✓');
      this.render();
    });
  }

  // ==========================================
  // VIEW 4: VISÃO DO CLIENTE FINAL (#/p/:uuid)
  // ==========================================
  private async renderClientView(uuid: string): Promise<void> {
    const orc = storage.getOrcamentoByUuid(uuid);
    if (!orc) {
      this.container.innerHTML = `
        <div class="p-12 text-center space-y-4">
          <p class="text-neutral-500 font-medium">Proposta não encontrada ou expirada.</p>
        </div>
      `;
      return;
    }

    // Registra evento de visualização caso ainda esteja 'enviado'
    if (orc.status === 'enviado') {
      storage.updateStatus(orc.id, 'visualizado', 'Proposta aberta e visualizada pelo cliente');
    }

    const qrSvg = await generateQrCodeSvg(orc.payloadPix?.payloadFormatado || orc.numero);

    this.container.innerHTML = `
      <div class="min-h-screen flex flex-col antialiased">
        ${this.portalHeaderHTML()}
        <!-- Top bar prestador -->
        <header class="glass-header px-4 py-3.5 sticky top-0 z-20 flex items-center justify-between shadow-sm">
          <div class="flex items-center gap-2.5">
            ${this.homeLink()}
            ${orc.perfilMei.logoUrl ? `
              <img src="${orc.perfilMei.logoUrl}" alt="Logo" class="w-9 h-9 rounded-xl object-contain bg-white p-1 border border-neutral-200 shadow-sm shrink-0" />
            ` : `
              <span class="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">⚡</span>
            `}
            <div>
              <span class="text-[10px] text-emerald-700 font-black uppercase tracking-wider block">PROPOSTA COMERCIAL</span>
              <strong class="text-sm font-black text-neutral-900 dark:text-white">${orc.perfilMei.nomeFantasia || orc.perfilMei.nome}</strong>
            </div>
          </div>
          <div class="flex items-center gap-2">
            ${this.chromeControls()}
            <button id="btn-imprimir-cliente" class="btn-touch btn-secondary text-xs px-2.5 py-1.5 font-bold" title="Imprimir ou Salvar Proposta em PDF">
              ${ICONS.print}
              <span>PDF</span>
            </button>
            <span class="${getStatusBadgeClass(orc.status)}">
              <span class="badge-dot"></span>
              ${getStatusLabel(orc.status)}
            </span>
          </div>
        </header>

        <main class="p-4 space-y-4 max-w-lg mx-auto w-full flex-1 pb-36">
          <!-- Banner de Status -->
          ${orc.status === 'sinal_pago' ? `
            <div class="bg-emerald-gradient text-white p-5 rounded-3xl text-center space-y-2 shadow-lg">
              <div class="w-12 h-12 rounded-full bg-white/20 mx-auto flex items-center justify-center text-white text-2xl font-bold">✓</div>
              <h3 class="font-black text-lg">${t('clientMarked')}</h3>
              <p class="text-xs max-w-xs mx-auto">${t('clientMarkedText')}</p>
            </div>
          ` : `
            <div class="card space-y-1.5 p-4 border-neutral-200">
              <span class="text-[11px] font-bold text-neutral-400 uppercase tracking-wider">${t('preparedFor')}</span>
              <h2 class="text-lg font-black text-neutral-900">${orc.cliente.nome}</h2>
              <p class="text-xs text-neutral-600 font-medium">${orc.titulo}</p>
            </div>
          `}

          <!-- Resumo de Serviços -->
          <div class="card p-4 space-y-3.5">
            <h3 class="text-xs font-bold text-neutral-400 uppercase tracking-wider">Serviços e Materiais Inclusos</h3>
            <div class="space-y-3 divide-y divide-neutral-100">
              ${orc.itens.map(item => `
                <div class="pt-2.5 flex justify-between items-start text-xs">
                  <div>
                    <strong class="text-neutral-900 block font-bold text-sm">${item.descricao}</strong>
                    <span class="text-neutral-500">${item.quantidade} ${item.unidade}</span>
                  </div>
                  <strong class="text-neutral-900 font-black text-sm">${formatCurrency(item.subtotal)}</strong>
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Resumo de Valores -->
          <div class="card p-4 space-y-3">
            <h3 class="text-xs font-bold text-neutral-400 uppercase tracking-wider">Investimento Total</h3>
            <div class="flex justify-between text-xs text-neutral-600 font-medium">
              <span>Valor Total dos Serviços:</span>
              <span class="font-black text-neutral-900 text-base">${formatCurrency(orc.valorTotal)}</span>
            </div>
            ${orc.valorSinal > 0 ? `
              <div class="pix-glow-box rounded-2xl p-4 flex justify-between items-center">
                <div>
                  <strong class="text-xs text-neutral-900 block font-black flex items-center gap-1">
                    ${ICONS.zap} Sinal Pix para Reserva (${orc.percentualSinal}%)
                  </strong>
                  <span class="text-[11px] text-neutral-500">Pague via Pix para garantir a data</span>
                </div>
                <strong class="text-lg font-black text-emerald-700">${formatCurrency(orc.valorSinal)}</strong>
              </div>
              <div class="flex justify-between text-xs text-neutral-500 px-1 font-medium">
                <span>Saldo restante na conclusão da obra:</span>
                <span class="font-bold text-neutral-700">${formatCurrency(orc.valorRestante)}</span>
              </div>
            ` : ''}
          </div>

          <!-- Observações & Garantia -->
          ${orc.observacoes ? `
            <div class="card p-4 text-xs space-y-1.5">
              <strong class="text-neutral-900 block font-bold flex items-center gap-1.5">
                ${ICONS.shield} Garantia & Termos do Serviço:
              </strong>
              <p class="text-neutral-600 leading-relaxed whitespace-pre-line">${orc.observacoes}</p>
            </div>
          ` : ''}
        </main>

        <!-- Sticky Bottom CTA -->
        <footer class="glass-header p-4 fixed bottom-0 left-0 right-0 max-w-lg mx-auto z-30 space-y-2 shadow-2xl">
          ${orc.status === 'sinal_pago' ? `
            <div class="text-center text-xs font-bold text-emerald-700 py-2 flex items-center justify-center gap-1.5">
              ${ICONS.check} O prestador marcou que recebeu o sinal
            </div>
          ` : `
            <button id="btn-abrir-bottomsheet-pix" class="btn-touch btn-primary w-full text-base font-black shadow-xl">
              ${ICONS.zap}
              <span>Pagar Sinal Pix (${formatCurrency(orc.valorSinal || orc.valorTotal)})</span>
            </button>
          `}
        </footer>

        <!-- Bottom Sheet Pix Modal -->
        <div id="bottom-sheet-pix" class="bottom-sheet-backdrop hidden">
          <div class="bottom-sheet-content max-w-lg mx-auto w-full space-y-4">
            <div class="bottom-sheet-handle"></div>
            
            <div class="text-center space-y-1">
              <span class="text-xs font-black text-emerald-600 uppercase tracking-wider">Pix na chave do prestador</span>
              <h3 class="text-lg font-black text-neutral-900">Copiar o código Pix</h3>
              <p class="text-xs text-neutral-500">Valor a transferir: <strong class="text-emerald-700 text-sm">${formatCurrency(orc.valorSinal || orc.valorTotal)}</strong></p>
              <p class="text-[11px] text-neutral-500">${t('pixSheetNote')}</p>
            </div>

            <!-- QR Code Frame -->
            <div class="flex justify-center p-3 pix-glow-box rounded-3xl">
              <div class="qr-plate w-52 h-52 p-2 rounded-2xl flex items-center justify-center shadow-inner">
                ${qrSvg}
              </div>
            </div>

            <!-- Copia e Cola -->
            <div class="space-y-2">
              <label class="block text-xs font-bold text-neutral-700">Código Pix Copia e Cola:</label>
              <div class="flex gap-2">
                <input type="text" readonly id="pix-copia-cola-input" class="input-touch text-xs font-mono bg-neutral-100 truncate" value="${orc.payloadPix?.payloadFormatado || ''}" />
                <button id="btn-copiar-pix-brcode" class="btn-touch btn-primary text-xs whitespace-nowrap px-4 font-bold shadow-md">
                  ${ICONS.copy}
                  <span>Copiar</span>
                </button>
              </div>
            </div>

            <div class="space-y-2 pt-2">
              <button id="btn-fechar-bottomsheet" class="btn-touch btn-ghost w-full text-xs font-bold text-neutral-500">
                Fechar
              </button>
            </div>
          </div>
        </div>
        ${this.portalFooterHTML()}
      </div>
    `;

    // Event Bindings
    this.bindChrome();
    const bottomSheet = this.container.querySelector('#bottom-sheet-pix') as HTMLElement;
    this.container.querySelector('#btn-abrir-bottomsheet-pix')?.addEventListener('click', () => {
      bottomSheet.classList.remove('hidden');
    });

    this.container.querySelector('#btn-fechar-bottomsheet')?.addEventListener('click', () => {
      bottomSheet.classList.add('hidden');
    });

    this.container.querySelector('#btn-copiar-pix-brcode')?.addEventListener('click', () => {
      const input = this.container.querySelector('#pix-copia-cola-input') as HTMLInputElement;
      if (input) {
        input.select();
        navigator.clipboard.writeText(input.value);
        this.showToast('Código Pix copiado com sucesso! Abra seu app do banco.', '✓');
      }
    });

    this.container.querySelector('#btn-imprimir-cliente')?.addEventListener('click', () => {
      printProposal(orc);
    });
  }

  // ==========================================
  // MODAL WHATSAPP COMPARTILHAMENTO MODERNO
  // ==========================================
  private openWhatsAppModal(id: string): void {
    const orc = storage.getOrcamentoById(id);
    if (!orc) return;

    const publicUrl = `${window.location.origin}${window.location.pathname}#/p/${orc.uuid}`;
    const mensagemComEmojis = buildWhatsAppQuoteMessage(orc, publicUrl);
    const mensagemLimpa = buildCleanWhatsAppQuoteMessage(orc, publicUrl);

    const existing = document.getElementById('whatsapp-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'whatsapp-modal';
    modal.className = 'fixed inset-0 z-50 flex items-center justify-center p-4 transition-all z-dialog-backdrop';
    modal.innerHTML = `
      <div class="z-dialog max-w-md w-full p-6 space-y-4">
        <div class="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-3">
          <div class="flex items-center gap-2">
            <span class="text-emerald-500 text-lg">${ICONS.whatsapp}</span>
            <div>
              <h3 class="text-base font-extrabold text-neutral-900 dark:text-white">Enviar pelo WhatsApp</h3>
              <p class="text-xs text-neutral-500 font-mono font-bold">${orc.cliente.telefone}</p>
            </div>
          </div>
          <button id="modal-zap-fechar-x" class="text-neutral-400 hover:text-neutral-700 dark:hover:text-white p-2 rounded-xl text-lg font-bold">✕</button>
        </div>

        <!-- Seletor de Formato Anti-Bug -->
        <div class="flex items-center justify-between p-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-xs">
          <span class="font-medium text-neutral-600 dark:text-neutral-300">Modo de Texto:</span>
          <div class="flex gap-1">
            <button type="button" id="btn-zap-modo-emojis" class="px-2.5 py-1 rounded-lg font-bold text-[11px] bg-emerald-600 text-white shadow-sm transition-all">Com Emojis</button>
            <button type="button" id="btn-zap-modo-limpo" class="px-2.5 py-1 rounded-lg font-semibold text-[11px] text-neutral-500 hover:text-neutral-800 dark:hover:text-white transition-all">Anti-'?' (Compatível Windows)</button>
          </div>
        </div>

        <div class="space-y-1.5">
          <label class="block text-xs font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Mensagem Formatada:</label>
          <textarea id="modal-zap-text" class="input-touch text-xs leading-relaxed font-mono w-full" rows="11"></textarea>
        </div>

        <p class="text-[11px] text-neutral-500 dark:text-neutral-400 leading-tight">
          💡 <strong>Dica de compatibilidade:</strong> Se no WhatsApp no computador os ícones ficarem com "?", use o modo <em>Anti-'?'</em> ou clique em <strong>Copiar Mensagem</strong> para colar direto no chat.
        </p>

        <div class="space-y-2 pt-1">
          <a id="modal-zap-link" href="${generateWhatsAppShareUrl(orc.cliente.telefone, mensagemComEmojis)}" target="_blank" class="btn-touch btn-whatsapp w-full font-black text-sm shadow-md">
            ${ICONS.whatsapp}
            <span>Abrir WhatsApp e Enviar</span>
          </a>
          <button id="modal-zap-copiar" class="btn-touch btn-secondary w-full text-xs font-bold flex items-center justify-center gap-2">
            ${ICONS.copy}
            <span>📋 Copiar Mensagem Completa</span>
          </button>
          <button id="modal-zap-fechar" class="btn-touch btn-ghost w-full text-xs font-bold text-neutral-500">
            Fechar
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const textarea = modal.querySelector('#modal-zap-text') as HTMLTextAreaElement;
    const zapLink = modal.querySelector('#modal-zap-link') as HTMLAnchorElement;
    const btnModoEmojis = modal.querySelector('#btn-zap-modo-emojis') as HTMLButtonElement;
    const btnModoLimpo = modal.querySelector('#btn-zap-modo-limpo') as HTMLButtonElement;

    textarea.value = mensagemComEmojis;

    const atualizarModo = (modo: 'padrao' | 'limpo') => {
      if (modo === 'padrao') {
        textarea.value = mensagemComEmojis;
        btnModoEmojis.className = 'px-2.5 py-1 rounded-lg font-bold text-[11px] bg-emerald-600 text-white shadow-sm transition-all';
        btnModoLimpo.className = 'px-2.5 py-1 rounded-lg font-semibold text-[11px] text-neutral-500 hover:text-neutral-800 dark:hover:text-white transition-all';
      } else {
        textarea.value = mensagemLimpa;
        btnModoLimpo.className = 'px-2.5 py-1 rounded-lg font-bold text-[11px] bg-emerald-600 text-white shadow-sm transition-all';
        btnModoEmojis.className = 'px-2.5 py-1 rounded-lg font-semibold text-[11px] text-neutral-500 hover:text-neutral-800 dark:hover:text-white transition-all';
      }
      zapLink.href = generateWhatsAppShareUrl(orc.cliente.telefone, textarea.value);
    };

    btnModoEmojis?.addEventListener('click', () => atualizarModo('padrao'));
    btnModoLimpo?.addEventListener('click', () => atualizarModo('limpo'));

    textarea.addEventListener('input', () => {
      zapLink.href = generateWhatsAppShareUrl(orc.cliente.telefone, textarea.value);
    });

    modal.querySelector('#modal-zap-copiar')?.addEventListener('click', () => {
      navigator.clipboard.writeText(textarea.value);
      this.showToast('Mensagem copiada para a área de transferência!', '📋');
    });

    modal.querySelector('#modal-zap-fechar')?.addEventListener('click', () => modal.remove());
    modal.querySelector('#modal-zap-fechar-x')?.addEventListener('click', () => modal.remove());

    zapLink.addEventListener('click', () => {
      storage.updateStatus(orc.id, 'enviado', 'Proposta compartilhada via WhatsApp');
      modal.remove();
    });
  }

  public openAuthModal(): void {
    const user = getCurrentUser();
    const existing = document.getElementById('auth-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'auth-modal';
    modal.className = 'fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-all animate-fade-in overflow-y-auto z-dialog-backdrop';

    if (user) {
      const outboxItems = storage.getOutbox();
      const pendingCount = outboxItems.filter(i => !i.synced).length;

      modal.innerHTML = `
        <div class="z-dialog bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 text-neutral-900 dark:text-white">
          <div class="z-dialog-head flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-3">
            <div class="flex items-center gap-2.5">
              <div class="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold">
                ✓
              </div>
              <div>
                <h3 class="font-bold text-base">Minha Conta ZapOrca</h3>
                <p class="text-xs text-neutral-500 dark:text-neutral-400">Perfil neste aparelho</p>
              </div>
            </div>
            <button id="btn-close-auth-modal" class="text-neutral-400 hover:text-neutral-700 dark:hover:text-white p-2 rounded-xl text-lg font-bold">✕</button>
          </div>

          <div class="bg-neutral-50 dark:bg-neutral-800/50 rounded-2xl p-4 space-y-2 border border-neutral-100 dark:border-neutral-800">
            <div>
              <span class="text-[10px] font-bold uppercase tracking-wider text-neutral-400 block">Nome do Prestador</span>
              <span class="font-bold text-sm text-neutral-900 dark:text-white">${user.nome || 'Não informado'}</span>
            </div>
            <div>
              <span class="text-[10px] font-bold uppercase tracking-wider text-neutral-400 block">E-mail Cadastrado</span>
              <span class="font-mono text-xs text-neutral-700 dark:text-neutral-300">${user.email}</span>
            </div>
            <div class="pt-2 border-t border-neutral-200/50 dark:border-neutral-700/50">
              <span class="text-[10px] font-bold uppercase tracking-wider text-emerald-600 block flex items-center gap-1">
                ${ICONS.shield} Consentimento LGPD Registrado (v${user.lgpdVersion})
              </span>
              <span class="text-[11px] text-neutral-500 dark:text-neutral-400 block">
                Aceite em: ${new Date(user.lgpdConsentAt).toLocaleString('pt-BR')}
              </span>
            </div>
          </div>

          <div class="p-3.5 rounded-2xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/70 dark:border-emerald-800/50">
            <span class="text-xs font-bold text-neutral-900 dark:text-white flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
              Dados neste aparelho
            </span>
            <span class="text-[11px] text-neutral-500 dark:text-neutral-400 block">
              ${pendingCount > 0 ? `${pendingCount} alteração(ões) guardadas só neste navegador.` : 'Nada é enviado para uma nuvem.'}
            </span>
          </div>

          <button id="btn-open-perfil-modal-from-auth" class="btn-touch btn-secondary w-full text-xs py-2.5 font-bold flex items-center justify-center gap-1.5 border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all">
            <span>🏢 Editar Dados da Minha Empresa & Pix</span>
          </button>

          <div class="flex gap-3">
            <button id="btn-logout-auth" class="btn-touch btn-secondary flex-1 text-xs py-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 font-bold">
              Sair da Conta
            </button>
            <button id="btn-close-auth-action" class="btn-touch btn-primary flex-1 text-xs py-3 font-bold">
              Fechar
            </button>
          </div>

          <div class="pt-2 border-t border-neutral-100 dark:border-neutral-800 text-center">
            <button id="btn-open-delete-account-modal" class="text-[11px] text-red-500 hover:text-red-700 dark:hover:text-red-400 font-semibold inline-flex items-center gap-1.5 py-1 px-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
              <span>⚠️ Excluir Conta & Descarte de Dados (LGPD)</span>
            </button>
          </div>
        </div>
      `;
    } else {
      modal.innerHTML = `
        <div class="z-dialog bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 text-neutral-900 dark:text-white">
          <div class="z-dialog-head flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-3">
            <div class="flex items-center gap-2.5">
              <div class="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold">
                👤
              </div>
              <div>
                <h3 class="font-bold text-base">Acesse sua Conta</h3>
                <p class="text-xs text-neutral-500 dark:text-neutral-400">Opcional. O orçamento funciona sem conta.</p>
              </div>
            </div>
            <button id="btn-close-auth-modal" class="text-neutral-400 hover:text-neutral-700 dark:hover:text-white p-2 rounded-xl text-lg font-bold">✕</button>
          </div>

          <!-- Abas de Navegação Login vs Cadastro -->
          <div class="flex border-b border-neutral-200 dark:border-neutral-700">
            <button id="tab-login" class="flex-1 pb-2.5 font-bold text-xs border-b-2 border-emerald-500 text-emerald-600 dark:text-emerald-400 transition-all">
              Entrar
            </button>
            <button id="tab-register" class="flex-1 pb-2.5 font-bold text-xs border-b-2 border-transparent text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-all">
              Criar Conta Gratuita
            </button>
          </div>

          <!-- SEÇÃO LOGIN -->
          <div id="section-login" class="space-y-4">
            <form id="form-login-account" class="space-y-4">
              <div>
                <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">E-mail</label>
                <input type="email" id="login-email" required class="input-touch w-full" placeholder="seuemail@exemplo.com" />
              </div>

              <div>
                <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Senha</label>
                <input type="password" id="login-senha" required class="input-touch w-full" placeholder="Sua senha cadastrada" />
              </div>

              <div id="login-error-msg" class="text-xs text-red-600 dark:text-red-400 font-semibold hidden"></div>

              <button type="submit" id="btn-submit-login" class="btn-touch btn-primary w-full py-3.5 text-sm font-bold shadow-md flex items-center justify-center gap-2">
                <span>Entrar neste aparelho</span>
              </button>

              <p class="text-[11px] text-center text-neutral-500 dark:text-neutral-400">
                Ainda não tem conta? <button type="button" id="btn-switch-to-reg" class="text-emerald-600 dark:text-emerald-400 font-bold hover:underline">Cadastre-se grátis</button>
              </p>
            </form>
          </div>

          <!-- SEÇÃO CADASTRO -->
          <div id="section-register" class="space-y-4 hidden">
            <form id="form-register-account" class="space-y-4">
              <div>
                <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Nome Completo</label>
                <input type="text" id="reg-nome" required minlength="3" class="input-touch w-full" placeholder="Ex: Jeremias Cardoso" />
              </div>

              <div>
                <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">E-mail Profissional</label>
                <input type="email" id="reg-email" required class="input-touch w-full" placeholder="seuemail@exemplo.com" />
              </div>

              <div>
                <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Senha de Acesso</label>
                <input type="password" id="reg-senha" required minlength="6" class="input-touch w-full" placeholder="Mínimo de 6 dígitos" />
              </div>

              <!-- Box de Consentimento LGPD Estrito -->
              <div class="p-3.5 rounded-2xl bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700 space-y-2">
                <label class="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" id="reg-lgpd" required class="mt-1 w-5 h-5 rounded text-emerald-600 focus:ring-emerald-500 border-neutral-300 dark:border-neutral-600 cursor-pointer" />
                  <span class="text-xs text-neutral-700 dark:text-neutral-200 leading-relaxed font-medium">
                    Concordo com os <strong>Termos de Uso</strong> e <strong>Política de Privacidade</strong> em conformidade com a <strong>LGPD (Lei 13.709/2018)</strong>.
                  </span>
                </label>
                <p class="text-[11px] text-neutral-500 dark:text-neutral-400 pl-7 leading-relaxed">
                  O perfil fica neste aparelho para nomear suas propostas. O Pix continua na sua chave. Não há conta na nuvem.
                </p>
              </div>

              <div id="reg-error-msg" class="text-xs text-red-600 dark:text-red-400 font-semibold hidden"></div>

              <button type="submit" id="btn-submit-register" class="btn-touch btn-primary w-full py-3.5 text-sm font-bold shadow-md flex items-center justify-center gap-2">
                <span>Criar Conta & Vincular Orçamentos</span>
              </button>

              <p class="text-[11px] text-center text-neutral-500 dark:text-neutral-400">
                Já possui uma conta? <button type="button" id="btn-switch-to-log" class="text-emerald-600 dark:text-emerald-400 font-bold hover:underline">Fazer login</button>
              </p>
            </form>
          </div>
        </div>
      `;
    }

    document.body.appendChild(modal);

    modal.querySelector('#btn-close-auth-modal')?.addEventListener('click', () => modal.remove());
    modal.querySelector('#btn-close-auth-action')?.addEventListener('click', () => modal.remove());
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.remove();
    });

    modal.querySelector('#btn-logout-auth')?.addEventListener('click', async () => {
      await logout();
      modal.remove();
      this.showToast('Sessão encerrada com sucesso', '👋');
      this.render();
    });

    modal.querySelector('#btn-open-perfil-modal-from-auth')?.addEventListener('click', () => {
      modal.remove();
      this.openPerfilEmpresaModal();
    });

    modal.querySelector('#btn-open-delete-account-modal')?.addEventListener('click', () => {
      modal.remove();
      if (user) {
        this.openDeleteAccountModal(user);
      }
    });

    // Controle de Abas Login vs Cadastro
    const tabLogin = modal.querySelector('#tab-login') as HTMLButtonElement | null;
    const tabRegister = modal.querySelector('#tab-register') as HTMLButtonElement | null;
    const sectionLogin = modal.querySelector('#section-login') as HTMLElement | null;
    const sectionRegister = modal.querySelector('#section-register') as HTMLElement | null;

    const switchToRegister = () => {
      if (!tabRegister || !tabLogin || !sectionRegister || !sectionLogin) return;
      tabRegister.classList.add('border-emerald-500', 'text-emerald-600', 'dark:text-emerald-400');
      tabRegister.classList.remove('border-transparent', 'text-neutral-400');
      tabLogin.classList.remove('border-emerald-500', 'text-emerald-600', 'dark:text-emerald-400');
      tabLogin.classList.add('border-transparent', 'text-neutral-400');
      sectionRegister.classList.remove('hidden');
      sectionLogin.classList.add('hidden');
    };

    const switchToLogin = () => {
      if (!tabRegister || !tabLogin || !sectionRegister || !sectionLogin) return;
      tabLogin.classList.add('border-emerald-500', 'text-emerald-600', 'dark:text-emerald-400');
      tabLogin.classList.remove('border-transparent', 'text-neutral-400');
      tabRegister.classList.remove('border-emerald-500', 'text-emerald-600', 'dark:text-emerald-400');
      tabRegister.classList.add('border-transparent', 'text-neutral-400');
      sectionLogin.classList.remove('hidden');
      sectionRegister.classList.add('hidden');
    };

    tabLogin?.addEventListener('click', switchToLogin);
    tabRegister?.addEventListener('click', switchToRegister);
    modal.querySelector('#btn-switch-to-reg')?.addEventListener('click', switchToRegister);
    modal.querySelector('#btn-switch-to-log')?.addEventListener('click', switchToLogin);

    // Formulário de Login
    const formLogin = modal.querySelector('#form-login-account') as HTMLFormElement | null;
    formLogin?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = (modal.querySelector('#login-email') as HTMLInputElement).value.trim();
      const password = (modal.querySelector('#login-senha') as HTMLInputElement).value;
      const errorEl = modal.querySelector('#login-error-msg') as HTMLElement;
      errorEl.classList.add('hidden');

      const submitBtn = modal.querySelector('#btn-submit-login') as HTMLButtonElement;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Autenticando...';

      try {
        const result = await loginAccount({ email, password });
        if (!result.success) {
          errorEl.textContent = result.error || 'Credenciais inválidas.';
          errorEl.classList.remove('hidden');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Entrar neste aparelho';
          return;
        }

        modal.remove();
        this.showToast(result.warning || `Bem-vindo, ${result.user?.nome || 'Prestador'}!`, '✓');
        this.render();
      } catch (err: any) {
        errorEl.textContent = err?.message || 'Erro inesperado ao efetuar login.';
        errorEl.classList.remove('hidden');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Entrar neste aparelho';
      }
    });

    // Formulário de Cadastro
    const formRegister = modal.querySelector('#form-register-account') as HTMLFormElement | null;
    formRegister?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const nomeCompleto = (modal.querySelector('#reg-nome') as HTMLInputElement).value.trim();
      const email = (modal.querySelector('#reg-email') as HTMLInputElement).value.trim();
      const password = (modal.querySelector('#reg-senha') as HTMLInputElement).value;
      const aceiteLgpd = (modal.querySelector('#reg-lgpd') as HTMLInputElement).checked;

      const errorEl = modal.querySelector('#reg-error-msg') as HTMLElement;
      errorEl.classList.add('hidden');

      if (!aceiteLgpd) {
        errorEl.textContent = 'Você deve aceitar os termos de consentimento LGPD para continuar.';
        errorEl.classList.remove('hidden');
        return;
      }

      const submitBtn = modal.querySelector('#btn-submit-register') as HTMLButtonElement;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Criando conta e vinculando...';

      try {
        const result = await registerAccount({
          nomeCompleto,
          email,
          password,
          aceiteLgpd: true
        });

        if (!result.success) {
          errorEl.textContent = result.error || 'Erro ao registrar conta.';
          errorEl.classList.remove('hidden');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Criar Conta & Vincular Orçamentos';
          return;
        }

        modal.remove();
        this.showToast(result.warning || 'Conta criada e orçamentos vinculados com sucesso!', '✓');
        this.render();
      } catch (err: any) {
        errorEl.textContent = err?.message || 'Erro inesperado ao registrar conta.';
        errorEl.classList.remove('hidden');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Criar Conta & Vincular Orçamentos';
      }
    });
  }

  public openPerfilEmpresaModal(): void {
    const perfil = storage.getPerfilMei();
    const existing = document.getElementById('perfil-empresa-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'perfil-empresa-modal';
    modal.className = 'fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-all animate-fade-in overflow-y-auto z-dialog-backdrop';

    modal.innerHTML = `
      <div class="z-dialog bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-3xl p-6 max-w-lg w-full shadow-2xl space-y-5 text-neutral-900 dark:text-white my-4">
        <div class="z-dialog-head flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-3">
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold">
              🏢
            </div>
            <div>
              <h3 class="font-bold text-base">Perfil da Minha Empresa</h3>
              <p class="text-xs text-neutral-500 dark:text-neutral-400">Dados do MEI e Chave Pix de Recebimento</p>
            </div>
          </div>
          <button id="btn-close-empresa-modal" class="text-neutral-400 hover:text-neutral-700 dark:hover:text-white p-2 rounded-xl text-lg font-bold">✕</button>
        </div>

        <!-- Status do Passaporte ZapOrca Pro -->
        <div class="p-3.5 rounded-2xl ${
          isZapOrcaProActive()
            ? 'bg-amber-500/10 border border-amber-500/30'
            : 'bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700'
        } flex items-center justify-between">
          <div class="flex items-center gap-2.5">
            <span class="text-xl">${isZapOrcaProActive() ? '✨' : '⚡'}</span>
            <div>
              <div class="text-xs font-bold text-neutral-900 dark:text-white flex items-center gap-1.5">
                <span>${isZapOrcaProActive() ? 'Passaporte ZapOrca Pro Ativo' : 'Plano Grátis (Até 5 propostas)'}</span>
                ${isZapOrcaProActive() ? '<span class="text-[9px] bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 font-bold px-1.5 py-0.2 rounded-full uppercase">Oficial</span>' : ''}
              </div>
              <div class="text-[10px] text-neutral-400">
                ${isZapOrcaProActive() ? 'Orçamentos ilimitados, sem marca d’água & exportação CSV' : 'Ative o Pro por R$ 29,90/ano sem renovação automática'}
              </div>
            </div>
          </div>
          <button type="button" id="btn-perfil-gerenciar-pro" class="btn-touch px-3 py-1.5 rounded-xl text-xs font-bold ${
            isZapOrcaProActive()
              ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-400/40 hover:bg-amber-500/30'
              : 'btn-primary shadow-sm'
          }">
            ${isZapOrcaProActive() ? 'Ver Licença' : '✨ Obter Pro'}
          </button>
        </div>

        <form id="form-perfil-empresa" class="space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="sm:col-span-2">
              <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Razão Social / Nome Completo *</label>
              <input type="text" id="emp-nome" required value="${perfil.nome}" class="input-touch w-full" placeholder="Ex: Jeremias Cardoso" />
            </div>

            <div class="sm:col-span-2">
              <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Nome Fantasia (Marca Comercial)</label>
              <input type="text" id="emp-nome-fantasia" value="${perfil.nomeFantasia || ''}" class="input-touch w-full" placeholder="Ex: Jeremias Elétrica & Pintura" />
            </div>

            <!-- Upload ou Escolha de Logotipo da Empresa -->
            <div class="sm:col-span-2 p-3.5 rounded-2xl bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700">
              <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">Logotipo da Empresa (Branding do Orçamento)</label>
              <div class="flex items-center gap-4">
                <div id="emp-logo-preview-box" class="w-16 h-16 rounded-xl border border-dashed border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                  ${perfil.logoUrl ? `<img src="${perfil.logoUrl}" id="emp-logo-img" alt="Logo" class="w-full h-full object-contain p-1" />` : `<span id="emp-logo-placeholder" class="text-2xl text-neutral-400">🖼️</span>`}
                </div>
                <div class="space-y-2 flex-1">
                  <div class="flex flex-wrap items-center gap-2">
                    <label for="emp-logo-input" class="btn-touch text-xs font-semibold px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer inline-flex items-center gap-1.5 shadow-sm transition-all">
                      <span>📁 Enviar Imagem</span>
                    </label>
                    <button type="button" id="btn-abrir-preset-logos" class="btn-touch text-xs font-semibold px-3 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white cursor-pointer inline-flex items-center gap-1.5 shadow-sm transition-all">
                      <span>🎨 Escolher da Biblioteca</span>
                    </button>
                    <button type="button" id="btn-remover-logo" class="btn-touch text-xs font-medium px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-red-600 transition-all ${perfil.logoUrl ? '' : 'hidden'}">
                      Remover
                    </button>
                  </div>
                  <input type="file" id="emp-logo-input" accept="image/png,image/jpeg,image/webp,image/svg+xml" class="hidden" />
                  <p class="text-[11px] text-neutral-500 leading-tight">Envie sua logo ou escolha um modelo profissional pronto para o seu ramo.</p>
                </div>
              </div>

              <!-- Grade de Logotipos Prontos (Biblioteca) -->
              <div id="box-preset-logos" class="hidden mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-700">
                <p class="text-xs font-bold text-neutral-700 dark:text-neutral-300 mb-2">Clique em um logotipo para aplicar à sua empresa:</p>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1 bg-neutral-100 dark:bg-neutral-900/60 rounded-xl">
                  ${PRESET_LOGOS.map(p => `
                    <div class="preset-logo-card p-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 flex items-center gap-2 cursor-pointer hover:border-emerald-500 transition-all" data-preset-id="${p.id}" title="${p.ramo}">
                      <img src="${p.svgDataUrl}" alt="${p.nome}" class="w-8 h-8 rounded-md shrink-0 object-contain bg-slate-900 p-0.5" />
                      <div class="overflow-hidden">
                        <div class="text-[11px] font-bold text-neutral-900 dark:text-white truncate">${p.nome}</div>
                        <div class="text-[9px] text-neutral-400 truncate">${p.icone} ${p.ramo.split(' ')[0]}</div>
                      </div>
                    </div>
                  `).join('')}
                </div>
              </div>
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">CPF ou CNPJ *</label>
              <input type="text" id="emp-documento" required value="${perfil.documento}" class="input-touch w-full" placeholder="00.000.000/0001-00" />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Telefone / WhatsApp *</label>
              <input type="text" id="emp-telefone" required value="${perfil.telefone}" class="input-touch w-full" placeholder="11987654321" />
            </div>

            <div class="sm:col-span-2">
              <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">E-mail Profissional</label>
              <input type="email" id="emp-email" value="${perfil.email || ''}" class="input-touch w-full" placeholder="contato@empresa.com" />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Ramo de Atividade *</label>
              <input type="text" id="emp-ramo" required value="${perfil.ramoAtividade}" class="input-touch w-full" placeholder="Ex: Eletricista Residencial" />
            </div>

            <div class="grid grid-cols-3 gap-2">
              <div class="col-span-2">
                <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Cidade *</label>
                <input type="text" id="emp-cidade" required maxlength="15" value="${perfil.cidade}" class="input-touch w-full" placeholder="São Paulo" />
              </div>
              <div>
                <label class="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">UF *</label>
                <input type="text" id="emp-uf" required maxlength="2" value="${perfil.uf}" class="input-touch w-full uppercase text-center" placeholder="SP" />
              </div>
            </div>
          </div>

          <!-- Box de Configuração da Chave Pix -->
          <div class="p-4 rounded-2xl bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700 space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-neutral-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                ${ICONS.zap} Chave Pix para Recebimento do Sinal
              </span>
              <span id="emp-pix-status" class="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400">
                ✓ Válida
              </span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label class="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Tipo de Chave</label>
                <select id="emp-tipo-pix" class="input-touch w-full text-xs">
                  <option value="telefone" ${perfil.tipoChavePix === 'telefone' ? 'selected' : ''}>Telefone (Celular)</option>
                  <option value="cpf" ${perfil.tipoChavePix === 'cpf' ? 'selected' : ''}>CPF</option>
                  <option value="cnpj" ${perfil.tipoChavePix === 'cnpj' ? 'selected' : ''}>CNPJ</option>
                  <option value="email" ${perfil.tipoChavePix === 'email' ? 'selected' : ''}>E-mail</option>
                  <option value="aleatoria" ${perfil.tipoChavePix === 'aleatoria' ? 'selected' : ''}>Chave Aleatória (EVP)</option>
                </select>
              </div>
              <div class="sm:col-span-2">
                <label class="block text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Chave Pix *</label>
                <input type="text" id="emp-chave-pix" required value="${perfil.chavePix}" class="input-touch w-full text-xs" placeholder="Informe a chave Pix" />
              </div>
            </div>
            <p id="emp-pix-helper" class="text-[11px] text-neutral-500 dark:text-neutral-400 leading-relaxed">
              Esta chave será convertida diretamente no BRCode oficial com CRC16 nos orçamentos gerados.
            </p>
          </div>

          <div id="emp-error-msg" class="text-xs text-red-600 dark:text-red-400 font-semibold hidden"></div>

          <div class="flex gap-3 pt-2">
            <button type="button" id="btn-cancel-empresa" class="btn-touch btn-secondary flex-1 py-3 text-xs font-bold">
              Cancelar
            </button>
            <button type="submit" id="btn-save-empresa" class="btn-touch btn-primary flex-1 py-3 text-xs font-bold shadow-md flex items-center justify-center gap-1.5">
              <span>Salvar neste aparelho</span>
            </button>
          </div>
        </form>
      </div>
    `;

    document.body.appendChild(modal);

    const closeModal = () => modal.remove();
    modal.querySelector('#btn-close-empresa-modal')?.addEventListener('click', closeModal);
    modal.querySelector('#btn-cancel-empresa')?.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    modal.querySelector('#btn-perfil-gerenciar-pro')?.addEventListener('click', () => {
      modal.remove();
      openZapOrcaProModal({
        onAtivado: () => {
          this.showToast('Passaporte ZapOrca Pro ativado com sucesso!', '✨');
          this.render();
        },
        onDesativado: () => {
          this.showToast('Licença Pro desativada.', 'ℹ️');
          this.render();
        },
      });
    });

    // Controle de Logotipo da Empresa
    const logoInput = modal.querySelector('#emp-logo-input') as HTMLInputElement | null;
    const btnRemoverLogo = modal.querySelector('#btn-remover-logo') as HTMLButtonElement | null;
    const previewBox = modal.querySelector('#emp-logo-preview-box') as HTMLElement | null;
    let currentLogoUrl: string | undefined = perfil.logoUrl;

    const updateLogoPreview = (url?: string) => {
      if (previewBox) {
        if (url) {
          previewBox.innerHTML = `<img src="${url}" alt="Logo" class="w-full h-full object-contain p-1" />`;
          btnRemoverLogo?.classList.remove('hidden');
        } else {
          previewBox.innerHTML = `<span class="text-2xl text-neutral-400">🖼️</span>`;
          btnRemoverLogo?.classList.add('hidden');
        }
      }
    };

    logoInput?.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        this.showToast('Imagem muito grande (máximo 5 MB)', '⚠️');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const rawDataUrl = event.target?.result as string;
        if (file.type === 'image/svg+xml') {
          currentLogoUrl = rawDataUrl;
          updateLogoPreview(currentLogoUrl);
        } else {
          const img = new Image();
          img.onload = () => {
            const maxW = 360;
            const maxH = 180;
            let w = img.width;
            let h = img.height;
            if (w > maxW || h > maxH) {
              const ratio = Math.min(maxW / w, maxH / h);
              w = Math.round(w * ratio);
              h = Math.round(h * ratio);
            }
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0, w, h);
              currentLogoUrl = canvas.toDataURL('image/png');
              updateLogoPreview(currentLogoUrl);
            }
          };
          img.src = rawDataUrl;
        }
      };
      reader.readAsDataURL(file);
    });

    btnRemoverLogo?.addEventListener('click', () => {
      currentLogoUrl = undefined;
      if (logoInput) logoInput.value = '';
      updateLogoPreview(undefined);
    });

    const btnAbrirPresets = modal.querySelector('#btn-abrir-preset-logos') as HTMLButtonElement | null;
    const boxPresets = modal.querySelector('#box-preset-logos') as HTMLElement | null;

    btnAbrirPresets?.addEventListener('click', () => {
      boxPresets?.classList.toggle('hidden');
    });

    modal.querySelectorAll('.preset-logo-card').forEach((card) => {
      card.addEventListener('click', () => {
        const presetId = (card as HTMLElement).dataset.presetId;
        const preset = PRESET_LOGOS.find(p => p.id === presetId);
        if (preset) {
          currentLogoUrl = preset.svgDataUrl;
          if (logoInput) logoInput.value = '';
          updateLogoPreview(currentLogoUrl);
          boxPresets?.classList.add('hidden');
          const ramoInput = modal.querySelector('#emp-ramo') as HTMLInputElement | null;
          if (ramoInput && !ramoInput.value.trim()) {
            ramoInput.value = preset.ramo;
          }
          this.showToast(`Logotipo "${preset.nome}" selecionado!`, '🎨');
        }
      });
    });

    const tipoSelect = modal.querySelector('#emp-tipo-pix') as HTMLSelectElement;
    const chaveInput = modal.querySelector('#emp-chave-pix') as HTMLInputElement;
    const pixStatus = modal.querySelector('#emp-pix-status') as HTMLElement;
    const pixHelper = modal.querySelector('#emp-pix-helper') as HTMLElement;

    const updatePixValidation = () => {
      const tipo = tipoSelect.value as TipoChavePix;
      const chave = chaveInput.value;
      const res = validatePixKey(chave, tipo);

      if (res.valid) {
        pixStatus.className = 'text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400';
        pixStatus.textContent = '✓ Válida';
        pixHelper.textContent = 'Esta chave será convertida diretamente no BRCode oficial com CRC16 nos orçamentos gerados.';
        pixHelper.className = 'text-[11px] text-neutral-500 dark:text-neutral-400 leading-relaxed';
      } else {
        pixStatus.className = 'text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950/60 text-red-700 dark:text-red-400';
        pixStatus.textContent = '✕ Inválida';
        pixHelper.textContent = res.error || 'Formato de chave inválido para o tipo selecionado';
        pixHelper.className = 'text-[11px] text-red-600 dark:text-red-400 leading-relaxed font-semibold';
      }
    };

    tipoSelect.addEventListener('change', updatePixValidation);
    chaveInput.addEventListener('input', updatePixValidation);
    updatePixValidation();

    const form = modal.querySelector('#form-perfil-empresa') as HTMLFormElement;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const nome = (modal.querySelector('#emp-nome') as HTMLInputElement).value.trim();
      const nomeFantasia = (modal.querySelector('#emp-nome-fantasia') as HTMLInputElement).value.trim();
      const documento = (modal.querySelector('#emp-documento') as HTMLInputElement).value.trim();
      const telefone = (modal.querySelector('#emp-telefone') as HTMLInputElement).value.trim();
      const email = (modal.querySelector('#emp-email') as HTMLInputElement).value.trim();
      const ramoAtividade = (modal.querySelector('#emp-ramo') as HTMLInputElement).value.trim();
      const cidade = (modal.querySelector('#emp-cidade') as HTMLInputElement).value.trim();
      const uf = (modal.querySelector('#emp-uf') as HTMLInputElement).value.trim().toUpperCase();
      const tipoChavePix = tipoSelect.value as TipoChavePix;
      const chavePix = chaveInput.value.trim();

      const validation = validatePerfilEmpresa({
        nome,
        nomeFantasia,
        documento,
        telefone,
        email,
        ramoAtividade,
        cidade,
        uf,
        tipoChavePix,
        chavePix
      });

      const errorEl = modal.querySelector('#emp-error-msg') as HTMLElement;
      errorEl.classList.add('hidden');

      if (!validation.valid) {
        const firstKey = Object.keys(validation.errors)[0];
        errorEl.textContent = validation.errors[firstKey] || 'Dados do perfil inválidos.';
        errorEl.classList.remove('hidden');
        return;
      }

      const saveBtn = modal.querySelector('#btn-save-empresa') as HTMLButtonElement;
      saveBtn.disabled = true;
      saveBtn.textContent = 'Salvando...';

      const novoPerfil: PerfilMei = {
        id: perfil.id,
        nome,
        nomeFantasia,
        documento,
        telefone,
        email,
        ramoAtividade,
        cidade,
        uf,
        tipoChavePix,
        chavePix,
        logoUrl: currentLogoUrl
      };

      storage.savePerfilMei(novoPerfil);
      await syncEmpresaData();

      closeModal();
      this.showToast('Perfil da empresa atualizado com sucesso!', '🏢');
      this.render();
    });
  }

  private openDeleteAccountModal(user: { id: string; email: string; nome?: string }): void {
    const existing = document.getElementById('modal-delete-account');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'modal-delete-account';
    modal.className = 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto z-dialog-backdrop';
    modal.innerHTML = `
      <div class="z-dialog bg-white dark:bg-neutral-900 border-2 border-red-500/40 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 text-neutral-900 dark:text-white animate-scale-up">
        <div class="z-dialog-head flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-3">
          <div class="flex items-center gap-2.5">
            <div class="w-10 h-10 rounded-2xl bg-red-100 dark:bg-red-950/60 text-red-600 flex items-center justify-center font-bold text-lg">
              ⚠️
            </div>
            <div>
              <h3 class="font-bold text-base text-red-600 dark:text-red-400">Excluir Conta & Dados</h3>
              <p class="text-[11px] text-neutral-500 dark:text-neutral-400">Direito de Eliminação — Art. 18, VI da LGPD</p>
            </div>
          </div>
          <button id="btn-close-delete-modal" class="text-neutral-400 hover:text-neutral-700 dark:hover:text-white p-2 rounded-xl text-lg font-bold">✕</button>
        </div>

        <div class="p-4 rounded-2xl bg-red-50/70 dark:bg-red-950/30 border border-red-200 dark:border-red-900/60 space-y-2 text-xs leading-relaxed text-red-900 dark:text-red-200">
          <p class="font-bold">Atenção: Esta ação é permanente e irreversível.</p>
          <ul class="list-disc pl-4 space-y-1 text-[11px] text-red-800 dark:text-red-300">
            <li>Todos os seus orçamentos salvos serão destruídos definitivamente.</li>
            <li>Os dados da sua empresa e chaves Pix serão apagados.</li>
            <li>O perfil e a fila local deste aparelho serão apagados.</li>
          </ul>
        </div>

        <div class="space-y-2">
          <label class="block text-xs font-semibold text-neutral-700 dark:text-neutral-300">
            Para confirmar a exclusão definitiva, digite exatamente o seu e-mail:
            <span class="block font-mono font-bold text-neutral-900 dark:text-white mt-0.5">${user.email}</span>
          </label>
          <input
            type="email"
            id="input-confirm-delete-email"
            class="input-touch w-full text-xs font-mono"
            placeholder="Digite ${user.email}"
            autocomplete="off"
          />
          <div id="delete-match-feedback" class="text-[11px] font-semibold text-neutral-400">
            Aguardando confirmação do e-mail...
          </div>
        </div>

        <div id="delete-error-msg" class="text-xs text-red-600 dark:text-red-400 font-semibold hidden"></div>

        <div class="flex gap-3 pt-2">
          <button type="button" id="btn-cancel-delete" class="btn-touch btn-secondary flex-1 py-3 text-xs font-bold">
            Cancelar
          </button>
          <button
            type="button"
            id="btn-confirm-delete-account"
            disabled
            class="btn-touch flex-1 py-3 text-xs font-bold rounded-2xl transition-all opacity-40 cursor-not-allowed bg-red-600 text-white shadow-md"
          >
            Excluir Definitivamente
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const closeModal = () => modal.remove();
    modal.querySelector('#btn-close-delete-modal')?.addEventListener('click', closeModal);
    modal.querySelector('#btn-cancel-delete')?.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    const emailInput = modal.querySelector('#input-confirm-delete-email') as HTMLInputElement;
    const confirmBtn = modal.querySelector('#btn-confirm-delete-account') as HTMLButtonElement;
    const matchFeedback = modal.querySelector('#delete-match-feedback') as HTMLElement;
    const errorEl = modal.querySelector('#delete-error-msg') as HTMLElement;

    const checkMatch = () => {
      const typed = emailInput.value.trim().toLowerCase();
      const expected = user.email.trim().toLowerCase();

      if (typed === expected) {
        confirmBtn.disabled = false;
        confirmBtn.className = 'btn-touch flex-1 py-3 text-xs font-bold rounded-2xl transition-all bg-red-600 hover:bg-red-700 active:scale-95 text-white shadow-md cursor-pointer';
        matchFeedback.textContent = '✓ E-mail confirmado. Ação de exclusão liberada.';
        matchFeedback.className = 'text-[11px] font-semibold text-emerald-600 dark:text-emerald-400';
      } else {
        confirmBtn.disabled = true;
        confirmBtn.className = 'btn-touch flex-1 py-3 text-xs font-bold rounded-2xl transition-all opacity-40 cursor-not-allowed bg-red-600 text-white shadow-md';
        if (typed.length > 0) {
          matchFeedback.textContent = '✕ O e-mail digitado não coincide.';
          matchFeedback.className = 'text-[11px] font-semibold text-amber-600 dark:text-amber-400';
        } else {
          matchFeedback.textContent = 'Aguardando confirmação do e-mail...';
          matchFeedback.className = 'text-[11px] font-semibold text-neutral-400';
        }
      }
    };

    emailInput.addEventListener('input', checkMatch);

    confirmBtn.addEventListener('click', async () => {
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Descartando dados...';
      errorEl.classList.add('hidden');

      try {
        const res = await deleteAccountAndUserData(emailInput.value.trim());
        if (!res.success) {
          errorEl.textContent = res.error || 'Falha ao processar exclusão da conta.';
          errorEl.classList.remove('hidden');
          confirmBtn.disabled = false;
          confirmBtn.textContent = 'Excluir Definitivamente';
          return;
        }

        closeModal();
        this.showToast('Conta e dados excluídos com sucesso (LGPD).', '🗑️');
        this.render();
      } catch (err: any) {
        errorEl.textContent = err?.message || 'Erro inesperado durante a exclusão.';
        errorEl.classList.remove('hidden');
        confirmBtn.disabled = false;
        confirmBtn.textContent = 'Excluir Definitivamente';
      }
    });
  }
}


